import {
  CURRENT_VERSION,
  TO_CLIENT_VERSIONED,
  TO_SERVER_VERSIONED
} from "./chunk-2EHQNR2O.js";
import {
  ActorContext,
  KEYS
} from "./chunk-7LY4X5LS.js";
import {
  LogLevelSchema,
  VERSION,
  assertUnreachable,
  bufferToArrayBuffer,
  deconstructError,
  getEnvUniversal,
  getLogHeaders,
  getLogger,
  getNodeEnv,
  getRivetEndpoint,
  getRivetEngine,
  getRivetNamespace,
  getRivetPublicEndpoint,
  getRivetPublicToken,
  getRivetRunEngine,
  getRivetRunEngineVersion,
  getRivetRunner,
  getRivetRunnerKey,
  getRivetRunnerVersion,
  getRivetToken,
  getRivetTotalSlots,
  getRivetkitInspectorDisable,
  getRivetkitInspectorToken,
  isCborSerializable,
  isDev,
  noopNext,
  promiseWithResolvers,
  stringifyError
} from "./chunk-TR5V2X4T.js";
import {
  ConnStateNotEnabled,
  Forbidden,
  IncomingMessageTooLong,
  InternalError,
  InvalidEncoding,
  InvalidParams,
  InvalidRequest,
  InvalidStateType,
  MalformedMessage,
  MissingActorHeader,
  OutgoingMessageTooLong,
  RestrictedFeature,
  Unreachable,
  Unsupported,
  WebSocketsNotEnabled
} from "./chunk-PY7SOKTM.js";

// src/common/actor-router-consts.ts
var PATH_CONNECT = "/connect";
var PATH_WEBSOCKET_BASE = "/websocket";
var PATH_WEBSOCKET_PREFIX = "/websocket/";
var PATH_INSPECTOR_CONNECT = "/inspector/connect";
var HEADER_ACTOR_QUERY = "x-rivet-query";
var HEADER_ENCODING = "x-rivet-encoding";
var HEADER_CONN_PARAMS = "x-rivet-conn-params";
var HEADER_ACTOR_ID = "x-rivet-actor";
var HEADER_RIVET_TOKEN = "x-rivet-token";
var HEADER_RIVET_TARGET = "x-rivet-target";
var HEADER_RIVET_ACTOR = "x-rivet-actor";
var HEADER_RIVET_NAMESPACE = "x-rivet-namespace";
var WS_PROTOCOL_STANDARD = "rivet";
var WS_PROTOCOL_TARGET = "rivet_target.";
var WS_PROTOCOL_ACTOR = "rivet_actor.";
var WS_PROTOCOL_ENCODING = "rivet_encoding.";
var WS_PROTOCOL_CONN_PARAMS = "rivet_conn_params.";
var WS_PROTOCOL_INSPECTOR_TOKEN = "rivet_inspector_token.";
var WS_TEST_PROTOCOL_PATH = "test_path.";
var ALLOWED_PUBLIC_HEADERS = [
  "Content-Type",
  "User-Agent",
  HEADER_ACTOR_QUERY,
  HEADER_ENCODING,
  HEADER_CONN_PARAMS,
  HEADER_ACTOR_ID,
  HEADER_RIVET_TARGET,
  HEADER_RIVET_ACTOR,
  HEADER_RIVET_NAMESPACE,
  HEADER_RIVET_TOKEN
];

// src/manager/router.ts
import { createRoute } from "@hono/zod-openapi";
import * as cbor6 from "cbor-x";
import invariant5 from "invariant";
import { z as z11 } from "zod/v4";

// src/actor/keys.ts
var EMPTY_KEY = "/";
var KEY_SEPARATOR = "/";
function serializeActorKey(key) {
  if (key.length === 0) {
    return EMPTY_KEY;
  }
  const escapedParts = key.map((part) => {
    if (part === "") {
      return "\\0";
    }
    let escaped = part.replace(/\\/g, "\\\\");
    escaped = escaped.replace(/\//g, `\\${KEY_SEPARATOR}`);
    return escaped;
  });
  return escapedParts.join(KEY_SEPARATOR);
}
function deserializeActorKey(keyString) {
  if (keyString === void 0 || keyString === null || keyString === EMPTY_KEY) {
    return [];
  }
  const parts = [];
  let currentPart = "";
  let escaping = false;
  let isEmptyStringMarker = false;
  for (let i = 0; i < keyString.length; i++) {
    const char = keyString[i];
    if (escaping) {
      if (char === "0") {
        isEmptyStringMarker = true;
      } else {
        currentPart += char;
      }
      escaping = false;
    } else if (char === "\\") {
      escaping = true;
    } else if (char === KEY_SEPARATOR) {
      if (isEmptyStringMarker) {
        parts.push("");
        isEmptyStringMarker = false;
      } else {
        parts.push(currentPart);
      }
      currentPart = "";
    } else {
      currentPart += char;
    }
  }
  if (escaping) {
    parts.push(currentPart + "\\");
  } else if (isEmptyStringMarker) {
    parts.push("");
  } else if (currentPart !== "" || parts.length > 0) {
    parts.push(currentPart);
  }
  return parts;
}

// src/common/router.ts
import * as cbor3 from "cbor-x";

// src/actor/router-endpoints.ts
import * as cbor2 from "cbor-x";

// src/actor/contexts/base/conn.ts
var ConnContext = class extends ActorContext {
  /**
   * @internal
   */
  constructor(actor, conn) {
    super(actor);
    this.conn = conn;
  }
};

// src/actor/contexts/action.ts
var ActionContext = class extends ConnContext {
};

// src/actor/contexts/base/conn-init.ts
var ConnInitContext = class extends ActorContext {
  /**
   * The incoming request that initiated the connection.
   * May be undefined for connections initiated without a direct HTTP request.
   */
  request;
  /**
   * @internal
   */
  constructor(actor, request) {
    super(actor);
    this.request = request;
  }
};

// src/actor/contexts/before-connect.ts
var BeforeConnectContext = class extends ConnInitContext {
};

// src/actor/contexts/connect.ts
var ConnectContext = class extends ConnContext {
};

// src/actor/contexts/create-conn-state.ts
var CreateConnStateContext = class extends ConnInitContext {
};

// src/actor/contexts/request.ts
var RequestContext = class extends ConnContext {
  /**
   * The incoming HTTP request.
   * May be undefined for request contexts initiated without a direct HTTP request.
   */
  request;
  /**
   * @internal
   */
  constructor(actor, conn, request) {
    super(actor, conn);
    this.request = request;
  }
};

// src/actor/contexts/websocket.ts
var WebSocketContext = class extends ConnContext {
  /**
   * The incoming HTTP request that initiated the WebSocket upgrade.
   * May be undefined for WebSocket connections initiated without a direct HTTP request.
   */
  request;
  /**
   * @internal
   */
  constructor(actor, conn, request) {
    super(actor, conn);
    this.request = request;
  }
};

// src/actor/protocol/serde.ts
import { z } from "zod/v4";

// src/serde.ts
import * as cbor from "cbor-x";
import invariant from "invariant";
function uint8ArrayToBase64(uint8Array) {
  if (typeof Buffer !== "undefined") {
    return Buffer.from(uint8Array).toString("base64");
  }
  let binary = "";
  const len = uint8Array.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binary);
}
function contentTypeForEncoding(encoding) {
  if (encoding === "json") {
    return "application/json";
  } else if (encoding === "cbor" || encoding === "bare") {
    return "application/octet-stream";
  } else {
    assertUnreachable(encoding);
  }
}
function serializeWithEncoding(encoding, value, versionedDataHandler, version, zodSchema, toJson, toBare) {
  if (encoding === "json") {
    const jsonValue = toJson(value);
    const validated = zodSchema.parse(jsonValue);
    return jsonStringifyCompat(validated);
  } else if (encoding === "cbor") {
    const jsonValue = toJson(value);
    const validated = zodSchema.parse(jsonValue);
    return cbor.encode(validated);
  } else if (encoding === "bare") {
    if (!versionedDataHandler) {
      throw new Error(
        "VersionedDataHandler is required for 'bare' encoding"
      );
    }
    if (version === void 0) {
      throw new Error("version is required for 'bare' encoding");
    }
    const bareValue = toBare(value);
    return versionedDataHandler.serializeWithEmbeddedVersion(
      bareValue,
      version
    );
  } else {
    assertUnreachable(encoding);
  }
}
function deserializeWithEncoding(encoding, buffer, versionedDataHandler, zodSchema, fromJson, fromBare) {
  if (encoding === "json") {
    let parsed;
    if (typeof buffer === "string") {
      parsed = jsonParseCompat(buffer);
    } else {
      const decoder = new TextDecoder("utf-8");
      const jsonString = decoder.decode(buffer);
      parsed = jsonParseCompat(jsonString);
    }
    const validated = zodSchema.parse(parsed);
    return fromJson(validated);
  } else if (encoding === "cbor") {
    invariant(
      typeof buffer !== "string",
      "buffer cannot be string for cbor encoding"
    );
    const decoded = cbor.decode(buffer);
    const validated = zodSchema.parse(decoded);
    return fromJson(validated);
  } else if (encoding === "bare") {
    invariant(
      typeof buffer !== "string",
      "buffer cannot be string for bare encoding"
    );
    if (!versionedDataHandler) {
      throw new Error(
        "VersionedDataHandler is required for 'bare' encoding"
      );
    }
    const bareValue = versionedDataHandler.deserializeWithEmbeddedVersion(buffer);
    return fromBare(bareValue);
  } else {
    assertUnreachable(encoding);
  }
}

// src/actor/log.ts
function loggerWithoutContext() {
  return getLogger("actor-runtime");
}

// src/actor/utils.ts
function assertUnreachable2(x) {
  loggerWithoutContext().error({
    msg: "unreachable",
    value: `${x}`,
    stack: new Error().stack
  });
  throw new Unreachable(x);
}
var DeadlineError = class extends Error {
  constructor() {
    super("Promise did not complete before deadline.");
  }
};
function deadline(promise, timeout) {
  const controller = new AbortController();
  const signal = controller.signal;
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      signal.addEventListener("abort", () => reject(new DeadlineError()));
    })
  ]).finally(() => {
    clearTimeout(timeoutId);
  });
}
function generateSecureToken(length = 32) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}
function isStatePath(path) {
  return path === "state" || path.startsWith("state.");
}
function isConnStatePath(path) {
  if (!path.startsWith("connections.")) {
    return false;
  }
  const stateIndex = path.indexOf(".state", 12);
  if (stateIndex === -1) {
    return false;
  }
  const afterState = stateIndex + 6;
  return path.length === afterState || path[afterState] === ".";
}

// src/actor/protocol/serde.ts
var EncodingSchema = z.enum(["json", "cbor", "bare"]);
var CachedSerializer = class {
  #data;
  #cache = /* @__PURE__ */ new Map();
  #versionedDataHandler;
  #version;
  #zodSchema;
  #toJson;
  #toBare;
  constructor(data, versionedDataHandler, version, zodSchema, toJson, toBare) {
    this.#data = data;
    this.#versionedDataHandler = versionedDataHandler;
    this.#version = version;
    this.#zodSchema = zodSchema;
    this.#toJson = toJson;
    this.#toBare = toBare;
  }
  get rawData() {
    return this.#data;
  }
  serialize(encoding) {
    const cached = this.#cache.get(encoding);
    if (cached) {
      return cached;
    } else {
      const serialized = serializeWithEncoding(
        encoding,
        this.#data,
        this.#versionedDataHandler,
        this.#version,
        this.#zodSchema,
        this.#toJson,
        this.#toBare
      );
      this.#cache.set(encoding, serialized);
      return serialized;
    }
  }
};
function base64EncodeUint8Array(uint8Array) {
  let binary = "";
  const len = uint8Array.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binary);
}
function base64EncodeArrayBuffer(arrayBuffer) {
  const uint8Array = new Uint8Array(arrayBuffer);
  return base64EncodeUint8Array(uint8Array);
}
function base64DecodeToUint8Array(base64) {
  if (typeof Buffer !== "undefined") {
    return new Uint8Array(Buffer.from(base64, "base64"));
  }
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
function base64DecodeToArrayBuffer(base64) {
  return base64DecodeToUint8Array(base64).buffer;
}
function jsonStringifyCompat(input) {
  return JSON.stringify(input, (_key, value) => {
    if (typeof value === "bigint") {
      return ["$BigInt", value.toString()];
    } else if (value instanceof ArrayBuffer) {
      return ["$ArrayBuffer", base64EncodeArrayBuffer(value)];
    } else if (value instanceof Uint8Array) {
      return ["$Uint8Array", base64EncodeUint8Array(value)];
    }
    if (Array.isArray(value) && value.length === 2 && typeof value[0] === "string" && value[0].startsWith("$")) {
      return ["$" + value[0], value[1]];
    }
    return value;
  });
}
function jsonParseCompat(input) {
  return JSON.parse(input, (_key, value) => {
    if (Array.isArray(value) && value.length === 2 && typeof value[0] === "string" && value[0].startsWith("$")) {
      if (value[0] === "$BigInt") {
        return BigInt(value[1]);
      } else if (value[0] === "$ArrayBuffer") {
        return base64DecodeToArrayBuffer(value[1]);
      } else if (value[0] === "$Uint8Array") {
        return base64DecodeToUint8Array(value[1]);
      }
      if (value[0].startsWith("$$")) {
        return [value[0].substring(1), value[1]];
      }
      throw new Error(
        `Unknown JSON encoding type: ${value[0]}. This may indicate corrupted data or a version mismatch.`
      );
    }
    return value;
  });
}

// src/schemas/client-protocol/versioned.ts
import { createVersionedDataHandler } from "vbare";

// dist/schemas/client-protocol/v1.ts
import * as bare from "@rivetkit/bare-ts";
var config = /* @__PURE__ */ bare.Config({});
function readInit(bc) {
  return {
    actorId: bare.readString(bc),
    connectionId: bare.readString(bc),
    connectionToken: bare.readString(bc)
  };
}
function writeInit(bc, x) {
  bare.writeString(bc, x.actorId);
  bare.writeString(bc, x.connectionId);
  bare.writeString(bc, x.connectionToken);
}
function read0(bc) {
  return bare.readBool(bc) ? bare.readData(bc) : null;
}
function write0(bc, x) {
  bare.writeBool(bc, x !== null);
  if (x !== null) {
    bare.writeData(bc, x);
  }
}
function read1(bc) {
  return bare.readBool(bc) ? bare.readUint(bc) : null;
}
function write1(bc, x) {
  bare.writeBool(bc, x !== null);
  if (x !== null) {
    bare.writeUint(bc, x);
  }
}
function readError(bc) {
  return {
    group: bare.readString(bc),
    code: bare.readString(bc),
    message: bare.readString(bc),
    metadata: read0(bc),
    actionId: read1(bc)
  };
}
function writeError(bc, x) {
  bare.writeString(bc, x.group);
  bare.writeString(bc, x.code);
  bare.writeString(bc, x.message);
  write0(bc, x.metadata);
  write1(bc, x.actionId);
}
function readActionResponse(bc) {
  return {
    id: bare.readUint(bc),
    output: bare.readData(bc)
  };
}
function writeActionResponse(bc, x) {
  bare.writeUint(bc, x.id);
  bare.writeData(bc, x.output);
}
function readEvent(bc) {
  return {
    name: bare.readString(bc),
    args: bare.readData(bc)
  };
}
function writeEvent(bc, x) {
  bare.writeString(bc, x.name);
  bare.writeData(bc, x.args);
}
function readToClientBody(bc) {
  const offset = bc.offset;
  const tag = bare.readU8(bc);
  switch (tag) {
    case 0:
      return { tag: "Init", val: readInit(bc) };
    case 1:
      return { tag: "Error", val: readError(bc) };
    case 2:
      return { tag: "ActionResponse", val: readActionResponse(bc) };
    case 3:
      return { tag: "Event", val: readEvent(bc) };
    default: {
      bc.offset = offset;
      throw new bare.BareError(offset, "invalid tag");
    }
  }
}
function writeToClientBody(bc, x) {
  switch (x.tag) {
    case "Init": {
      bare.writeU8(bc, 0);
      writeInit(bc, x.val);
      break;
    }
    case "Error": {
      bare.writeU8(bc, 1);
      writeError(bc, x.val);
      break;
    }
    case "ActionResponse": {
      bare.writeU8(bc, 2);
      writeActionResponse(bc, x.val);
      break;
    }
    case "Event": {
      bare.writeU8(bc, 3);
      writeEvent(bc, x.val);
      break;
    }
  }
}
function readToClient(bc) {
  return {
    body: readToClientBody(bc)
  };
}
function writeToClient(bc, x) {
  writeToClientBody(bc, x.body);
}
function encodeToClient(x) {
  const bc = new bare.ByteCursor(
    new Uint8Array(config.initialBufferLength),
    config
  );
  writeToClient(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeToClient(bytes) {
  const bc = new bare.ByteCursor(bytes, config);
  const result = readToClient(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readActionRequest(bc) {
  return {
    id: bare.readUint(bc),
    name: bare.readString(bc),
    args: bare.readData(bc)
  };
}
function writeActionRequest(bc, x) {
  bare.writeUint(bc, x.id);
  bare.writeString(bc, x.name);
  bare.writeData(bc, x.args);
}
function readSubscriptionRequest(bc) {
  return {
    eventName: bare.readString(bc),
    subscribe: bare.readBool(bc)
  };
}
function writeSubscriptionRequest(bc, x) {
  bare.writeString(bc, x.eventName);
  bare.writeBool(bc, x.subscribe);
}
function readToServerBody(bc) {
  const offset = bc.offset;
  const tag = bare.readU8(bc);
  switch (tag) {
    case 0:
      return { tag: "ActionRequest", val: readActionRequest(bc) };
    case 1:
      return { tag: "SubscriptionRequest", val: readSubscriptionRequest(bc) };
    default: {
      bc.offset = offset;
      throw new bare.BareError(offset, "invalid tag");
    }
  }
}
function writeToServerBody(bc, x) {
  switch (x.tag) {
    case "ActionRequest": {
      bare.writeU8(bc, 0);
      writeActionRequest(bc, x.val);
      break;
    }
    case "SubscriptionRequest": {
      bare.writeU8(bc, 1);
      writeSubscriptionRequest(bc, x.val);
      break;
    }
  }
}
function readToServer(bc) {
  return {
    body: readToServerBody(bc)
  };
}
function writeToServer(bc, x) {
  writeToServerBody(bc, x.body);
}
function encodeToServer(x) {
  const bc = new bare.ByteCursor(
    new Uint8Array(config.initialBufferLength),
    config
  );
  writeToServer(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeToServer(bytes) {
  const bc = new bare.ByteCursor(bytes, config);
  const result = readToServer(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpActionRequest(bc) {
  return {
    args: bare.readData(bc)
  };
}
function writeHttpActionRequest(bc, x) {
  bare.writeData(bc, x.args);
}
function encodeHttpActionRequest(x) {
  const bc = new bare.ByteCursor(
    new Uint8Array(config.initialBufferLength),
    config
  );
  writeHttpActionRequest(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpActionRequest(bytes) {
  const bc = new bare.ByteCursor(bytes, config);
  const result = readHttpActionRequest(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpActionResponse(bc) {
  return {
    output: bare.readData(bc)
  };
}
function writeHttpActionResponse(bc, x) {
  bare.writeData(bc, x.output);
}
function encodeHttpActionResponse(x) {
  const bc = new bare.ByteCursor(
    new Uint8Array(config.initialBufferLength),
    config
  );
  writeHttpActionResponse(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpActionResponse(bytes) {
  const bc = new bare.ByteCursor(bytes, config);
  const result = readHttpActionResponse(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpResponseError(bc) {
  return {
    group: bare.readString(bc),
    code: bare.readString(bc),
    message: bare.readString(bc),
    metadata: read0(bc)
  };
}
function writeHttpResponseError(bc, x) {
  bare.writeString(bc, x.group);
  bare.writeString(bc, x.code);
  bare.writeString(bc, x.message);
  write0(bc, x.metadata);
}
function encodeHttpResponseError(x) {
  const bc = new bare.ByteCursor(
    new Uint8Array(config.initialBufferLength),
    config
  );
  writeHttpResponseError(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpResponseError(bytes) {
  const bc = new bare.ByteCursor(bytes, config);
  const result = readHttpResponseError(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpResolveResponse(bc) {
  return {
    actorId: bare.readString(bc)
  };
}
function writeHttpResolveResponse(bc, x) {
  bare.writeString(bc, x.actorId);
}
function encodeHttpResolveResponse(x) {
  const bc = new bare.ByteCursor(
    new Uint8Array(config.initialBufferLength),
    config
  );
  writeHttpResolveResponse(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpResolveResponse(bytes) {
  const bc = new bare.ByteCursor(bytes, config);
  const result = readHttpResolveResponse(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare.BareError(bc.offset, "remaining bytes");
  }
  return result;
}

// dist/schemas/client-protocol/v2.ts
import * as bare2 from "@rivetkit/bare-ts";
var config2 = /* @__PURE__ */ bare2.Config({});
function readInit2(bc) {
  return {
    actorId: bare2.readString(bc),
    connectionId: bare2.readString(bc)
  };
}
function writeInit2(bc, x) {
  bare2.writeString(bc, x.actorId);
  bare2.writeString(bc, x.connectionId);
}
function read02(bc) {
  return bare2.readBool(bc) ? bare2.readData(bc) : null;
}
function write02(bc, x) {
  bare2.writeBool(bc, x !== null);
  if (x !== null) {
    bare2.writeData(bc, x);
  }
}
function read12(bc) {
  return bare2.readBool(bc) ? bare2.readUint(bc) : null;
}
function write12(bc, x) {
  bare2.writeBool(bc, x !== null);
  if (x !== null) {
    bare2.writeUint(bc, x);
  }
}
function readError2(bc) {
  return {
    group: bare2.readString(bc),
    code: bare2.readString(bc),
    message: bare2.readString(bc),
    metadata: read02(bc),
    actionId: read12(bc)
  };
}
function writeError2(bc, x) {
  bare2.writeString(bc, x.group);
  bare2.writeString(bc, x.code);
  bare2.writeString(bc, x.message);
  write02(bc, x.metadata);
  write12(bc, x.actionId);
}
function readActionResponse2(bc) {
  return {
    id: bare2.readUint(bc),
    output: bare2.readData(bc)
  };
}
function writeActionResponse2(bc, x) {
  bare2.writeUint(bc, x.id);
  bare2.writeData(bc, x.output);
}
function readEvent2(bc) {
  return {
    name: bare2.readString(bc),
    args: bare2.readData(bc)
  };
}
function writeEvent2(bc, x) {
  bare2.writeString(bc, x.name);
  bare2.writeData(bc, x.args);
}
function readToClientBody2(bc) {
  const offset = bc.offset;
  const tag = bare2.readU8(bc);
  switch (tag) {
    case 0:
      return { tag: "Init", val: readInit2(bc) };
    case 1:
      return { tag: "Error", val: readError2(bc) };
    case 2:
      return { tag: "ActionResponse", val: readActionResponse2(bc) };
    case 3:
      return { tag: "Event", val: readEvent2(bc) };
    default: {
      bc.offset = offset;
      throw new bare2.BareError(offset, "invalid tag");
    }
  }
}
function writeToClientBody2(bc, x) {
  switch (x.tag) {
    case "Init": {
      bare2.writeU8(bc, 0);
      writeInit2(bc, x.val);
      break;
    }
    case "Error": {
      bare2.writeU8(bc, 1);
      writeError2(bc, x.val);
      break;
    }
    case "ActionResponse": {
      bare2.writeU8(bc, 2);
      writeActionResponse2(bc, x.val);
      break;
    }
    case "Event": {
      bare2.writeU8(bc, 3);
      writeEvent2(bc, x.val);
      break;
    }
  }
}
function readToClient2(bc) {
  return {
    body: readToClientBody2(bc)
  };
}
function writeToClient2(bc, x) {
  writeToClientBody2(bc, x.body);
}
function encodeToClient2(x) {
  const bc = new bare2.ByteCursor(
    new Uint8Array(config2.initialBufferLength),
    config2
  );
  writeToClient2(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeToClient2(bytes) {
  const bc = new bare2.ByteCursor(bytes, config2);
  const result = readToClient2(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare2.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readActionRequest2(bc) {
  return {
    id: bare2.readUint(bc),
    name: bare2.readString(bc),
    args: bare2.readData(bc)
  };
}
function writeActionRequest2(bc, x) {
  bare2.writeUint(bc, x.id);
  bare2.writeString(bc, x.name);
  bare2.writeData(bc, x.args);
}
function readSubscriptionRequest2(bc) {
  return {
    eventName: bare2.readString(bc),
    subscribe: bare2.readBool(bc)
  };
}
function writeSubscriptionRequest2(bc, x) {
  bare2.writeString(bc, x.eventName);
  bare2.writeBool(bc, x.subscribe);
}
function readToServerBody2(bc) {
  const offset = bc.offset;
  const tag = bare2.readU8(bc);
  switch (tag) {
    case 0:
      return { tag: "ActionRequest", val: readActionRequest2(bc) };
    case 1:
      return { tag: "SubscriptionRequest", val: readSubscriptionRequest2(bc) };
    default: {
      bc.offset = offset;
      throw new bare2.BareError(offset, "invalid tag");
    }
  }
}
function writeToServerBody2(bc, x) {
  switch (x.tag) {
    case "ActionRequest": {
      bare2.writeU8(bc, 0);
      writeActionRequest2(bc, x.val);
      break;
    }
    case "SubscriptionRequest": {
      bare2.writeU8(bc, 1);
      writeSubscriptionRequest2(bc, x.val);
      break;
    }
  }
}
function readToServer2(bc) {
  return {
    body: readToServerBody2(bc)
  };
}
function writeToServer2(bc, x) {
  writeToServerBody2(bc, x.body);
}
function encodeToServer2(x) {
  const bc = new bare2.ByteCursor(
    new Uint8Array(config2.initialBufferLength),
    config2
  );
  writeToServer2(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeToServer2(bytes) {
  const bc = new bare2.ByteCursor(bytes, config2);
  const result = readToServer2(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare2.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpActionRequest2(bc) {
  return {
    args: bare2.readData(bc)
  };
}
function writeHttpActionRequest2(bc, x) {
  bare2.writeData(bc, x.args);
}
function encodeHttpActionRequest2(x) {
  const bc = new bare2.ByteCursor(
    new Uint8Array(config2.initialBufferLength),
    config2
  );
  writeHttpActionRequest2(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpActionRequest2(bytes) {
  const bc = new bare2.ByteCursor(bytes, config2);
  const result = readHttpActionRequest2(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare2.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpActionResponse2(bc) {
  return {
    output: bare2.readData(bc)
  };
}
function writeHttpActionResponse2(bc, x) {
  bare2.writeData(bc, x.output);
}
function encodeHttpActionResponse2(x) {
  const bc = new bare2.ByteCursor(
    new Uint8Array(config2.initialBufferLength),
    config2
  );
  writeHttpActionResponse2(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpActionResponse2(bytes) {
  const bc = new bare2.ByteCursor(bytes, config2);
  const result = readHttpActionResponse2(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare2.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpResponseError2(bc) {
  return {
    group: bare2.readString(bc),
    code: bare2.readString(bc),
    message: bare2.readString(bc),
    metadata: read02(bc)
  };
}
function writeHttpResponseError2(bc, x) {
  bare2.writeString(bc, x.group);
  bare2.writeString(bc, x.code);
  bare2.writeString(bc, x.message);
  write02(bc, x.metadata);
}
function encodeHttpResponseError2(x) {
  const bc = new bare2.ByteCursor(
    new Uint8Array(config2.initialBufferLength),
    config2
  );
  writeHttpResponseError2(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpResponseError2(bytes) {
  const bc = new bare2.ByteCursor(bytes, config2);
  const result = readHttpResponseError2(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare2.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpResolveResponse2(bc) {
  return {
    actorId: bare2.readString(bc)
  };
}
function writeHttpResolveResponse2(bc, x) {
  bare2.writeString(bc, x.actorId);
}
function encodeHttpResolveResponse2(x) {
  const bc = new bare2.ByteCursor(
    new Uint8Array(config2.initialBufferLength),
    config2
  );
  writeHttpResolveResponse2(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpResolveResponse2(bytes) {
  const bc = new bare2.ByteCursor(bytes, config2);
  const result = readHttpResolveResponse2(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare2.BareError(bc.offset, "remaining bytes");
  }
  return result;
}

// dist/schemas/client-protocol/v3.ts
import * as bare3 from "@rivetkit/bare-ts";
var config3 = /* @__PURE__ */ bare3.Config({});
function readInit3(bc) {
  return {
    actorId: bare3.readString(bc),
    connectionId: bare3.readString(bc)
  };
}
function writeInit3(bc, x) {
  bare3.writeString(bc, x.actorId);
  bare3.writeString(bc, x.connectionId);
}
function read03(bc) {
  return bare3.readBool(bc) ? bare3.readData(bc) : null;
}
function write03(bc, x) {
  bare3.writeBool(bc, x !== null);
  if (x !== null) {
    bare3.writeData(bc, x);
  }
}
function read13(bc) {
  return bare3.readBool(bc) ? bare3.readUint(bc) : null;
}
function write13(bc, x) {
  bare3.writeBool(bc, x !== null);
  if (x !== null) {
    bare3.writeUint(bc, x);
  }
}
function readError3(bc) {
  return {
    group: bare3.readString(bc),
    code: bare3.readString(bc),
    message: bare3.readString(bc),
    metadata: read03(bc),
    actionId: read13(bc)
  };
}
function writeError3(bc, x) {
  bare3.writeString(bc, x.group);
  bare3.writeString(bc, x.code);
  bare3.writeString(bc, x.message);
  write03(bc, x.metadata);
  write13(bc, x.actionId);
}
function readActionResponse3(bc) {
  return {
    id: bare3.readUint(bc),
    output: bare3.readData(bc)
  };
}
function writeActionResponse3(bc, x) {
  bare3.writeUint(bc, x.id);
  bare3.writeData(bc, x.output);
}
function readEvent3(bc) {
  return {
    name: bare3.readString(bc),
    args: bare3.readData(bc)
  };
}
function writeEvent3(bc, x) {
  bare3.writeString(bc, x.name);
  bare3.writeData(bc, x.args);
}
function readToClientBody3(bc) {
  const offset = bc.offset;
  const tag = bare3.readU8(bc);
  switch (tag) {
    case 0:
      return { tag: "Init", val: readInit3(bc) };
    case 1:
      return { tag: "Error", val: readError3(bc) };
    case 2:
      return { tag: "ActionResponse", val: readActionResponse3(bc) };
    case 3:
      return { tag: "Event", val: readEvent3(bc) };
    default: {
      bc.offset = offset;
      throw new bare3.BareError(offset, "invalid tag");
    }
  }
}
function writeToClientBody3(bc, x) {
  switch (x.tag) {
    case "Init": {
      bare3.writeU8(bc, 0);
      writeInit3(bc, x.val);
      break;
    }
    case "Error": {
      bare3.writeU8(bc, 1);
      writeError3(bc, x.val);
      break;
    }
    case "ActionResponse": {
      bare3.writeU8(bc, 2);
      writeActionResponse3(bc, x.val);
      break;
    }
    case "Event": {
      bare3.writeU8(bc, 3);
      writeEvent3(bc, x.val);
      break;
    }
  }
}
function readToClient3(bc) {
  return {
    body: readToClientBody3(bc)
  };
}
function writeToClient3(bc, x) {
  writeToClientBody3(bc, x.body);
}
function encodeToClient3(x) {
  const bc = new bare3.ByteCursor(
    new Uint8Array(config3.initialBufferLength),
    config3
  );
  writeToClient3(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeToClient3(bytes) {
  const bc = new bare3.ByteCursor(bytes, config3);
  const result = readToClient3(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare3.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readActionRequest3(bc) {
  return {
    id: bare3.readUint(bc),
    name: bare3.readString(bc),
    args: bare3.readData(bc)
  };
}
function writeActionRequest3(bc, x) {
  bare3.writeUint(bc, x.id);
  bare3.writeString(bc, x.name);
  bare3.writeData(bc, x.args);
}
function readSubscriptionRequest3(bc) {
  return {
    eventName: bare3.readString(bc),
    subscribe: bare3.readBool(bc)
  };
}
function writeSubscriptionRequest3(bc, x) {
  bare3.writeString(bc, x.eventName);
  bare3.writeBool(bc, x.subscribe);
}
function readToServerBody3(bc) {
  const offset = bc.offset;
  const tag = bare3.readU8(bc);
  switch (tag) {
    case 0:
      return { tag: "ActionRequest", val: readActionRequest3(bc) };
    case 1:
      return { tag: "SubscriptionRequest", val: readSubscriptionRequest3(bc) };
    default: {
      bc.offset = offset;
      throw new bare3.BareError(offset, "invalid tag");
    }
  }
}
function writeToServerBody3(bc, x) {
  switch (x.tag) {
    case "ActionRequest": {
      bare3.writeU8(bc, 0);
      writeActionRequest3(bc, x.val);
      break;
    }
    case "SubscriptionRequest": {
      bare3.writeU8(bc, 1);
      writeSubscriptionRequest3(bc, x.val);
      break;
    }
  }
}
function readToServer3(bc) {
  return {
    body: readToServerBody3(bc)
  };
}
function writeToServer3(bc, x) {
  writeToServerBody3(bc, x.body);
}
function encodeToServer3(x) {
  const bc = new bare3.ByteCursor(
    new Uint8Array(config3.initialBufferLength),
    config3
  );
  writeToServer3(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeToServer3(bytes) {
  const bc = new bare3.ByteCursor(bytes, config3);
  const result = readToServer3(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare3.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpActionRequest3(bc) {
  return {
    args: bare3.readData(bc)
  };
}
function writeHttpActionRequest3(bc, x) {
  bare3.writeData(bc, x.args);
}
function encodeHttpActionRequest3(x) {
  const bc = new bare3.ByteCursor(
    new Uint8Array(config3.initialBufferLength),
    config3
  );
  writeHttpActionRequest3(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpActionRequest3(bytes) {
  const bc = new bare3.ByteCursor(bytes, config3);
  const result = readHttpActionRequest3(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare3.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpActionResponse3(bc) {
  return {
    output: bare3.readData(bc)
  };
}
function writeHttpActionResponse3(bc, x) {
  bare3.writeData(bc, x.output);
}
function encodeHttpActionResponse3(x) {
  const bc = new bare3.ByteCursor(
    new Uint8Array(config3.initialBufferLength),
    config3
  );
  writeHttpActionResponse3(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpActionResponse3(bytes) {
  const bc = new bare3.ByteCursor(bytes, config3);
  const result = readHttpActionResponse3(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare3.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function read2(bc) {
  return bare3.readBool(bc) ? bare3.readString(bc) : null;
}
function write2(bc, x) {
  bare3.writeBool(bc, x !== null);
  if (x !== null) {
    bare3.writeString(bc, x);
  }
}
function read3(bc) {
  return bare3.readBool(bc) ? bare3.readBool(bc) : null;
}
function write3(bc, x) {
  bare3.writeBool(bc, x !== null);
  if (x !== null) {
    bare3.writeBool(bc, x);
  }
}
function read4(bc) {
  return bare3.readBool(bc) ? bare3.readU64(bc) : null;
}
function write4(bc, x) {
  bare3.writeBool(bc, x !== null);
  if (x !== null) {
    bare3.writeU64(bc, x);
  }
}
function readHttpQueueSendRequest(bc) {
  return {
    body: bare3.readData(bc),
    name: read2(bc),
    wait: read3(bc),
    timeout: read4(bc)
  };
}
function writeHttpQueueSendRequest(bc, x) {
  bare3.writeData(bc, x.body);
  write2(bc, x.name);
  write3(bc, x.wait);
  write4(bc, x.timeout);
}
function encodeHttpQueueSendRequest(x) {
  const bc = new bare3.ByteCursor(
    new Uint8Array(config3.initialBufferLength),
    config3
  );
  writeHttpQueueSendRequest(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpQueueSendRequest(bytes) {
  const bc = new bare3.ByteCursor(bytes, config3);
  const result = readHttpQueueSendRequest(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare3.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpQueueSendResponse(bc) {
  return {
    status: bare3.readString(bc),
    response: read03(bc)
  };
}
function writeHttpQueueSendResponse(bc, x) {
  bare3.writeString(bc, x.status);
  write03(bc, x.response);
}
function encodeHttpQueueSendResponse(x) {
  const bc = new bare3.ByteCursor(
    new Uint8Array(config3.initialBufferLength),
    config3
  );
  writeHttpQueueSendResponse(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpQueueSendResponse(bytes) {
  const bc = new bare3.ByteCursor(bytes, config3);
  const result = readHttpQueueSendResponse(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare3.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpResponseError3(bc) {
  return {
    group: bare3.readString(bc),
    code: bare3.readString(bc),
    message: bare3.readString(bc),
    metadata: read03(bc)
  };
}
function writeHttpResponseError3(bc, x) {
  bare3.writeString(bc, x.group);
  bare3.writeString(bc, x.code);
  bare3.writeString(bc, x.message);
  write03(bc, x.metadata);
}
function encodeHttpResponseError3(x) {
  const bc = new bare3.ByteCursor(
    new Uint8Array(config3.initialBufferLength),
    config3
  );
  writeHttpResponseError3(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpResponseError3(bytes) {
  const bc = new bare3.ByteCursor(bytes, config3);
  const result = readHttpResponseError3(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare3.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readHttpResolveResponse3(bc) {
  return {
    actorId: bare3.readString(bc)
  };
}
function writeHttpResolveResponse3(bc, x) {
  bare3.writeString(bc, x.actorId);
}
function encodeHttpResolveResponse3(x) {
  const bc = new bare3.ByteCursor(
    new Uint8Array(config3.initialBufferLength),
    config3
  );
  writeHttpResolveResponse3(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeHttpResolveResponse3(bytes) {
  const bc = new bare3.ByteCursor(bytes, config3);
  const result = readHttpResolveResponse3(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare3.BareError(bc.offset, "remaining bytes");
  }
  return result;
}

// src/schemas/client-protocol/versioned.ts
var CURRENT_VERSION2 = 3;
var v1ToV2 = (v1Data) => {
  if (v1Data.body.tag === "Init") {
    const { actorId, connectionId } = v1Data.body.val;
    return {
      body: {
        tag: "Init",
        val: {
          actorId,
          connectionId
        }
      }
    };
  }
  return v1Data;
};
var v2ToV1 = (v2Data) => {
  if (v2Data.body.tag === "Init") {
    const { actorId, connectionId } = v2Data.body.val;
    return {
      body: {
        tag: "Init",
        val: {
          actorId,
          connectionId,
          connectionToken: ""
          // Add empty connectionToken for v1 compatibility
        }
      }
    };
  }
  return v2Data;
};
var v2ToV3 = (v2Data) => {
  return v2Data;
};
var v3ToV2 = (v3Data) => {
  return v3Data;
};
var v1ToServerV2 = (v1Data) => {
  return v1Data;
};
var v2ToServerV3 = (v2Data) => {
  return v2Data;
};
var v3ToServerV2 = (v3Data) => {
  return v3Data;
};
var v2ToServerV1 = (v2Data) => {
  return v2Data;
};
var TO_SERVER_VERSIONED2 = createVersionedDataHandler({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 1:
        return decodeToServer(bytes);
      case 2:
        return decodeToServer2(bytes);
      case 3:
        return decodeToServer3(bytes);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 1:
        return encodeToServer(data);
      case 2:
        return encodeToServer2(data);
      case 3:
        return encodeToServer3(data);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  deserializeConverters: () => [v1ToServerV2, v2ToServerV3],
  serializeConverters: () => [v3ToServerV2, v2ToServerV1]
});
var TO_CLIENT_VERSIONED2 = createVersionedDataHandler({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 1:
        return decodeToClient(bytes);
      case 2:
        return decodeToClient2(bytes);
      case 3:
        return decodeToClient3(bytes);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 1:
        return encodeToClient(data);
      case 2:
        return encodeToClient2(data);
      case 3:
        return encodeToClient3(data);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  deserializeConverters: () => [v1ToV2, v2ToV3],
  serializeConverters: () => [v3ToV2, v2ToV1]
});
var HTTP_ACTION_REQUEST_VERSIONED = createVersionedDataHandler({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 1:
        return decodeHttpActionRequest(bytes);
      case 2:
        return decodeHttpActionRequest2(bytes);
      case 3:
        return decodeHttpActionRequest3(bytes);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 1:
        return encodeHttpActionRequest(
          data
        );
      case 2:
        return encodeHttpActionRequest2(
          data
        );
      case 3:
        return encodeHttpActionRequest3(
          data
        );
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  deserializeConverters: () => [],
  serializeConverters: () => []
});
var HTTP_ACTION_RESPONSE_VERSIONED = createVersionedDataHandler({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 1:
        return decodeHttpActionResponse(bytes);
      case 2:
        return decodeHttpActionResponse2(bytes);
      case 3:
        return decodeHttpActionResponse3(bytes);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 1:
        return encodeHttpActionResponse(
          data
        );
      case 2:
        return encodeHttpActionResponse2(
          data
        );
      case 3:
        return encodeHttpActionResponse3(
          data
        );
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  deserializeConverters: () => [],
  serializeConverters: () => []
});
var HTTP_QUEUE_SEND_REQUEST_VERSIONED = createVersionedDataHandler({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 3:
        return decodeHttpQueueSendRequest(bytes);
      default:
        throw new Error(
          `HttpQueueSendRequest only exists in version 3+, got version ${version}`
        );
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 3:
        return encodeHttpQueueSendRequest(
          data
        );
      default:
        throw new Error(
          `HttpQueueSendRequest only exists in version 3+, got version ${version}`
        );
    }
  },
  deserializeConverters: () => [],
  serializeConverters: () => []
});
var HTTP_QUEUE_SEND_RESPONSE_VERSIONED = createVersionedDataHandler({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 3:
        return decodeHttpQueueSendResponse(bytes);
      default:
        throw new Error(
          `HttpQueueSendResponse only exists in version 3+, got version ${version}`
        );
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 3:
        return encodeHttpQueueSendResponse(
          data
        );
      default:
        throw new Error(
          `HttpQueueSendResponse only exists in version 3+, got version ${version}`
        );
    }
  },
  deserializeConverters: () => [],
  serializeConverters: () => []
});
var HTTP_RESPONSE_ERROR_VERSIONED = createVersionedDataHandler({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 1:
        return decodeHttpResponseError(bytes);
      case 2:
        return decodeHttpResponseError2(bytes);
      case 3:
        return decodeHttpResponseError3(bytes);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 1:
        return encodeHttpResponseError(
          data
        );
      case 2:
        return encodeHttpResponseError2(
          data
        );
      case 3:
        return encodeHttpResponseError3(
          data
        );
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  deserializeConverters: () => [],
  serializeConverters: () => []
});
var HTTP_RESOLVE_RESPONSE_VERSIONED = createVersionedDataHandler({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 1:
        return decodeHttpResolveResponse(bytes);
      case 2:
        return decodeHttpResolveResponse2(bytes);
      case 3:
        return decodeHttpResolveResponse3(bytes);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 1:
        return encodeHttpResolveResponse(
          data
        );
      case 2:
        return encodeHttpResolveResponse2(
          data
        );
      case 3:
        return encodeHttpResolveResponse3(
          data
        );
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  deserializeConverters: () => [],
  serializeConverters: () => []
});

// src/schemas/client-protocol-zod/mod.ts
import { z as z2 } from "zod/v4";
var UintSchema = z2.bigint();
var OptionalUintSchema = UintSchema.nullable();
var InitSchema = z2.object({
  actorId: z2.string(),
  connectionId: z2.string()
});
var ErrorSchema = z2.object({
  group: z2.string(),
  code: z2.string(),
  message: z2.string(),
  metadata: z2.unknown().optional(),
  actionId: OptionalUintSchema
});
var ActionResponseSchema = z2.object({
  id: UintSchema,
  output: z2.unknown()
});
var EventSchema = z2.object({
  name: z2.string(),
  args: z2.unknown()
});
var ToClientBodySchema = z2.discriminatedUnion("tag", [
  z2.object({ tag: z2.literal("Init"), val: InitSchema }),
  z2.object({ tag: z2.literal("Error"), val: ErrorSchema }),
  z2.object({ tag: z2.literal("ActionResponse"), val: ActionResponseSchema }),
  z2.object({ tag: z2.literal("Event"), val: EventSchema })
]);
var ToClientSchema = z2.object({
  body: ToClientBodySchema
});
var ActionRequestSchema = z2.object({
  id: UintSchema,
  name: z2.string(),
  args: z2.unknown()
});
var SubscriptionRequestSchema = z2.object({
  eventName: z2.string(),
  subscribe: z2.boolean()
});
var ToServerBodySchema = z2.discriminatedUnion("tag", [
  z2.object({ tag: z2.literal("ActionRequest"), val: ActionRequestSchema }),
  z2.object({
    tag: z2.literal("SubscriptionRequest"),
    val: SubscriptionRequestSchema
  })
]);
var ToServerSchema = z2.object({
  body: ToServerBodySchema
});
var HttpActionRequestSchema = z2.object({
  args: z2.unknown()
});
var HttpActionResponseSchema = z2.object({
  output: z2.unknown()
});
var HttpQueueSendRequestSchema = z2.object({
  body: z2.unknown(),
  name: z2.string().optional(),
  wait: z2.boolean().optional(),
  timeout: z2.number().optional()
});
var HttpQueueSendResponseSchema = z2.object({
  status: z2.enum(["completed", "timedOut"]),
  response: z2.unknown().optional()
});
var HttpResponseErrorSchema = z2.object({
  group: z2.string(),
  code: z2.string(),
  message: z2.string(),
  metadata: z2.unknown().optional()
});
var HttpResolveRequestSchema = z2.null();
var HttpResolveResponseSchema = z2.object({
  actorId: z2.string()
});

// src/actor/conn/drivers/http.ts
function createHttpDriver() {
  return {
    type: "http",
    getConnectionReadyState(_actor, _conn) {
      return 1 /* OPEN */;
    },
    disconnect: async () => {
    }
  };
}

// src/actor/conn/drivers/raw-request.ts
function createRawRequestDriver() {
  return {
    type: "raw-request",
    disconnect: async () => {
    },
    getConnectionReadyState: () => {
      return 1 /* OPEN */;
    }
  };
}

// src/actor/router-endpoints.ts
async function handleAction(c, config8, actorDriver, actionName, actorId) {
  const encoding = getRequestEncoding(c.req);
  const parameters = getRequestConnParams(c.req);
  const arrayBuffer = await c.req.arrayBuffer();
  if (arrayBuffer.byteLength > config8.maxIncomingMessageSize) {
    throw new IncomingMessageTooLong();
  }
  const request = deserializeWithEncoding(
    encoding,
    new Uint8Array(arrayBuffer),
    HTTP_ACTION_REQUEST_VERSIONED,
    HttpActionRequestSchema,
    // JSON: args is already the decoded value (raw object/array)
    (json) => json.args,
    // BARE/CBOR: args is ArrayBuffer that needs CBOR-decoding
    (bare8) => cbor2.decode(new Uint8Array(bare8.args))
  );
  const actionArgs = request;
  let actor;
  let conn;
  let output;
  try {
    actor = await actorDriver.loadActor(actorId);
    actor.rLog.debug({ msg: "handling action", actionName, encoding });
    conn = await actor.connectionManager.prepareAndConnectConn(
      createHttpDriver(),
      parameters,
      c.req.raw,
      c.req.path,
      c.req.header()
    );
    const ctx = new ActionContext(actor, conn);
    output = await actor.executeAction(ctx, actionName, actionArgs);
  } finally {
    if (conn) {
      conn.disconnect();
    }
  }
  const serialized = serializeWithEncoding(
    encoding,
    output,
    HTTP_ACTION_RESPONSE_VERSIONED,
    CURRENT_VERSION2,
    HttpActionResponseSchema,
    // JSON: output is the raw value (will be serialized by jsonStringifyCompat)
    (value) => ({ output: value }),
    // BARE/CBOR: output needs to be CBOR-encoded to ArrayBuffer
    (value) => ({
      output: bufferToArrayBuffer(cbor2.encode(value))
    })
  );
  const messageSize = serialized instanceof Uint8Array ? serialized.byteLength : serialized.length;
  if (messageSize > config8.maxOutgoingMessageSize) {
    throw new OutgoingMessageTooLong();
  }
  return c.body(serialized, 200, {
    "Content-Type": contentTypeForEncoding(encoding)
  });
}
async function handleQueueSend(c, config8, actorDriver, actorId, queueName) {
  const encoding = getRequestEncoding(c.req);
  const params = getRequestConnParams(c.req);
  const arrayBuffer = await c.req.arrayBuffer();
  if (arrayBuffer.byteLength > config8.maxIncomingMessageSize) {
    throw new IncomingMessageTooLong();
  }
  const request = deserializeWithEncoding(
    encoding,
    new Uint8Array(arrayBuffer),
    HTTP_QUEUE_SEND_REQUEST_VERSIONED,
    HttpQueueSendRequestSchema,
    (json) => json,
    (bare8) => ({
      name: bare8.name ?? void 0,
      body: cbor2.decode(new Uint8Array(bare8.body)),
      wait: bare8.wait ?? void 0,
      timeout: bare8.timeout !== null && bare8.timeout !== void 0 ? Number(bare8.timeout) : void 0
    })
  );
  const name = queueName ?? request.name;
  if (!name) {
    throw new InvalidRequest("missing queue name");
  }
  const actor = await actorDriver.loadActor(actorId);
  const conn = await actor.connectionManager.prepareAndConnectConn(
    createHttpDriver(),
    params,
    c.req.raw,
    c.req.path,
    c.req.header()
  );
  let result = {
    status: "completed"
  };
  try {
    if (request.wait) {
      result = await actor.queueManager.enqueueAndWait(
        name,
        request.body,
        request.timeout
      );
    } else {
      await actor.queueManager.enqueue(name, request.body);
    }
  } finally {
    conn.disconnect();
  }
  const response = serializeWithEncoding(
    encoding,
    result,
    HTTP_QUEUE_SEND_RESPONSE_VERSIONED,
    CURRENT_VERSION2,
    HttpQueueSendResponseSchema,
    (value) => ({
      status: value.status,
      response: value.response
    }),
    (value) => ({
      status: value.status,
      response: value.response !== void 0 ? bufferToArrayBuffer(cbor2.encode(value.response)) : null
    })
  );
  return c.body(response, 200, {
    "Content-Type": contentTypeForEncoding(encoding)
  });
}
async function handleRawRequest(c, req, actorDriver, actorId) {
  const actor = await actorDriver.loadActor(actorId);
  const parameters = getRequestConnParams(c.req);
  let createdConn;
  try {
    const conn = await actor.connectionManager.prepareAndConnectConn(
      createRawRequestDriver(),
      parameters,
      req,
      c.req.path,
      c.req.header()
    );
    createdConn = conn;
    return await actor.handleRawRequest(conn, req);
  } finally {
    if (createdConn) {
      createdConn.disconnect();
    }
  }
}
function getRequestEncoding(req) {
  const encodingParam = req.header(HEADER_ENCODING);
  if (!encodingParam) {
    return "json";
  }
  const result = EncodingSchema.safeParse(encodingParam);
  if (!result.success) {
    throw new InvalidEncoding(encodingParam);
  }
  return result.data;
}
function getRequestExposeInternalError(_req) {
  return getEnvUniversal("RIVET_EXPOSE_ERRORS") === "1" || getEnvUniversal("NODE_ENV") === "development";
}
function getRequestConnParams(req) {
  const paramsParam = req.header(HEADER_CONN_PARAMS);
  if (!paramsParam) {
    return null;
  }
  try {
    return JSON.parse(paramsParam);
  } catch (err) {
    throw new InvalidParams(
      `Invalid params JSON: ${stringifyError(err)}`
    );
  }
}

// src/registry/config/index.ts
import { z as z8 } from "zod/v4";

// src/actor/config.ts
import { z as z3 } from "zod/v4";
var zFunction = () => z3.custom((val) => typeof val === "function");
var WorkflowInspectorConfigSchema = z3.object({
  getHistory: zFunction(),
  onHistoryUpdated: zFunction().optional()
});
var RunInspectorConfigSchema = z3.object({
  workflow: WorkflowInspectorConfigSchema.optional()
}).optional();
var RunConfigSchema = z3.object({
  /** Display name for the actor in the Inspector UI. */
  name: z3.string().optional(),
  /** Icon for the actor in the Inspector UI. Can be an emoji or FontAwesome icon name. */
  icon: z3.string().optional(),
  /** The run handler function. */
  run: zFunction(),
  /** Inspector integration for long-running run handlers. */
  inspector: RunInspectorConfigSchema.optional()
});
var zRunHandler = z3.union([zFunction(), RunConfigSchema]).optional();
function getRunFunction(run) {
  if (!run) return void 0;
  if (typeof run === "function") return run;
  return run.run;
}
function getRunMetadata(run) {
  if (!run || typeof run === "function") return {};
  return { name: run.name, icon: run.icon };
}
function getRunInspectorConfig(run) {
  if (!run || typeof run === "function") return void 0;
  return run.inspector;
}
var ActorConfigSchema = z3.object({
  onCreate: zFunction().optional(),
  onDestroy: zFunction().optional(),
  onWake: zFunction().optional(),
  onSleep: zFunction().optional(),
  run: zRunHandler,
  onStateChange: zFunction().optional(),
  onBeforeConnect: zFunction().optional(),
  onConnect: zFunction().optional(),
  onDisconnect: zFunction().optional(),
  onBeforeActionResponse: zFunction().optional(),
  onRequest: zFunction().optional(),
  onWebSocket: zFunction().optional(),
  actions: z3.record(z3.string(), zFunction()).default(() => ({})),
  state: z3.any().optional(),
  createState: zFunction().optional(),
  connState: z3.any().optional(),
  createConnState: zFunction().optional(),
  vars: z3.any().optional(),
  db: z3.any().optional(),
  createVars: zFunction().optional(),
  options: z3.object({
    /** Display name for the actor in the Inspector UI. */
    name: z3.string().optional(),
    /** Icon for the actor in the Inspector UI. Can be an emoji or FontAwesome icon name. */
    icon: z3.string().optional(),
    createVarsTimeout: z3.number().positive().default(5e3),
    createConnStateTimeout: z3.number().positive().default(5e3),
    onConnectTimeout: z3.number().positive().default(5e3),
    // This must be less than ACTOR_STOP_THRESHOLD_MS
    onSleepTimeout: z3.number().positive().default(5e3),
    onDestroyTimeout: z3.number().positive().default(5e3),
    stateSaveInterval: z3.number().positive().default(1e4),
    actionTimeout: z3.number().positive().default(6e4),
    // Max time to wait for waitUntil background promises during shutdown
    waitUntilTimeout: z3.number().positive().default(15e3),
    // Max time to wait for run handler to stop during shutdown
    runStopTimeout: z3.number().positive().default(15e3),
    connectionLivenessTimeout: z3.number().positive().default(2500),
    connectionLivenessInterval: z3.number().positive().default(5e3),
    noSleep: z3.boolean().default(false),
    sleepTimeout: z3.number().positive().default(3e4),
    maxQueueSize: z3.number().positive().default(1e3),
    maxQueueMessageSize: z3.number().positive().default(1024 * 1024),
    /**
     * Can hibernate WebSockets for onWebSocket.
     *
     * WebSockets using actions/events are hibernatable by default.
     *
     * @experimental
     **/
    canHibernateWebSocket: z3.union([
      z3.boolean(),
      zFunction()
    ]).default(false)
  }).strict().prefault(() => ({}))
}).strict().refine(
  (data) => !(data.state !== void 0 && data.createState !== void 0),
  {
    message: "Cannot define both 'state' and 'createState'",
    path: ["state"]
  }
).refine(
  (data) => !(data.connState !== void 0 && data.createConnState !== void 0),
  {
    message: "Cannot define both 'connState' and 'createConnState'",
    path: ["connState"]
  }
).refine(
  (data) => !(data.vars !== void 0 && data.createVars !== void 0),
  {
    message: "Cannot define both 'vars' and 'createVars'",
    path: ["vars"]
  }
);
var DocActorOptionsSchema = z3.object({
  name: z3.string().optional().describe("Display name for the actor in the Inspector UI."),
  icon: z3.string().optional().describe(
    "Icon for the actor in the Inspector UI. Can be an emoji (e.g., '\u{1F680}') or FontAwesome icon name (e.g., 'rocket')."
  ),
  createVarsTimeout: z3.number().optional().describe("Timeout in ms for createVars handler. Default: 5000"),
  createConnStateTimeout: z3.number().optional().describe(
    "Timeout in ms for createConnState handler. Default: 5000"
  ),
  onConnectTimeout: z3.number().optional().describe("Timeout in ms for onConnect handler. Default: 5000"),
  onSleepTimeout: z3.number().optional().describe(
    "Timeout in ms for onSleep handler. Must be less than ACTOR_STOP_THRESHOLD_MS. Default: 5000"
  ),
  onDestroyTimeout: z3.number().optional().describe("Timeout in ms for onDestroy handler. Default: 5000"),
  stateSaveInterval: z3.number().optional().describe(
    "Interval in ms between automatic state saves. Default: 10000"
  ),
  actionTimeout: z3.number().optional().describe("Timeout in ms for action handlers. Default: 60000"),
  waitUntilTimeout: z3.number().optional().describe(
    "Max time in ms to wait for waitUntil background promises during shutdown. Default: 15000"
  ),
  runStopTimeout: z3.number().optional().describe(
    "Max time in ms to wait for run handler to stop during shutdown. Default: 15000"
  ),
  connectionLivenessTimeout: z3.number().optional().describe(
    "Timeout in ms for connection liveness checks. Default: 2500"
  ),
  connectionLivenessInterval: z3.number().optional().describe(
    "Interval in ms between connection liveness checks. Default: 5000"
  ),
  noSleep: z3.boolean().optional().describe("If true, the actor will never sleep. Default: false"),
  sleepTimeout: z3.number().optional().describe(
    "Time in ms of inactivity before the actor sleeps. Default: 30000"
  ),
  maxQueueSize: z3.number().optional().describe(
    "Maximum number of queue messages before rejecting new messages. Default: 1000"
  ),
  maxQueueMessageSize: z3.number().optional().describe(
    "Maximum size of each queue message in bytes. Default: 1048576"
  ),
  canHibernateWebSocket: z3.boolean().optional().describe(
    "Whether WebSockets using onWebSocket can be hibernated. WebSockets using actions/events are hibernatable by default. Default: false"
  )
}).describe("Actor options for timeouts and behavior configuration.");
var DocActorConfigSchema = z3.object({
  state: z3.unknown().optional().describe(
    "Initial state value for the actor. Cannot be used with createState."
  ),
  createState: z3.unknown().optional().describe(
    "Function to create initial state. Receives context and input. Cannot be used with state."
  ),
  connState: z3.unknown().optional().describe(
    "Initial connection state value. Cannot be used with createConnState."
  ),
  createConnState: z3.unknown().optional().describe(
    "Function to create connection state. Receives context and connection params. Cannot be used with connState."
  ),
  vars: z3.unknown().optional().describe(
    "Initial ephemeral variables value. Cannot be used with createVars."
  ),
  createVars: z3.unknown().optional().describe(
    "Function to create ephemeral variables. Receives context and driver context. Cannot be used with vars."
  ),
  db: z3.unknown().optional().describe("Database provider instance for the actor."),
  onCreate: z3.unknown().optional().describe(
    "Called when the actor is first initialized. Use to initialize state."
  ),
  onDestroy: z3.unknown().optional().describe("Called when the actor is destroyed."),
  onWake: z3.unknown().optional().describe(
    "Called when the actor wakes up and is ready to receive connections and actions."
  ),
  onSleep: z3.unknown().optional().describe(
    "Called when the actor is stopping or sleeping. Use to clean up resources."
  ),
  run: z3.unknown().optional().describe(
    "Called after actor starts. Does not block startup. Use for background tasks like queue processing or tick loops. If it exits or throws, the actor crashes."
  ),
  onStateChange: z3.unknown().optional().describe(
    "Called when the actor's state changes. State changes within this hook won't trigger recursion."
  ),
  onBeforeConnect: z3.unknown().optional().describe(
    "Called before a client connects. Throw an error to reject the connection."
  ),
  onConnect: z3.unknown().optional().describe("Called when a client successfully connects."),
  onDisconnect: z3.unknown().optional().describe("Called when a client disconnects."),
  onBeforeActionResponse: z3.unknown().optional().describe(
    "Called before sending an action response. Use to transform output."
  ),
  onRequest: z3.unknown().optional().describe(
    "Called for raw HTTP requests to /actors/{name}/http/* endpoints."
  ),
  onWebSocket: z3.unknown().optional().describe(
    "Called for raw WebSocket connections to /actors/{name}/websocket/* endpoints."
  ),
  actions: z3.record(z3.string(), z3.unknown()).optional().describe(
    "Map of action name to handler function. Defaults to an empty object."
  ),
  options: DocActorOptionsSchema.optional()
}).describe("Actor configuration passed to the actor() function.");

// src/engine-process/constants.ts
var ENGINE_PORT = 6420;
var ENGINE_ENDPOINT = `http://127.0.0.1:${ENGINE_PORT}`;

// src/inspector/config.ts
import { z as z4 } from "zod/v4";
var defaultTokenFn = () => {
  const envToken = getRivetkitInspectorToken();
  if (envToken) {
    return envToken;
  }
  return "";
};
var defaultEnabled = () => {
  return isDev() || !getRivetkitInspectorDisable();
};
var InspectorConfigSchema = z4.object({
  enabled: z4.boolean().default(defaultEnabled),
  /**
   * Token used to access the Inspector.
   */
  token: z4.custom().optional().default(() => defaultTokenFn),
  /**
   * Default RivetKit server endpoint for Rivet Inspector to connect to. This should be the same endpoint as what you use for your Rivet client to connect to RivetKit.
   *
   * This is a convenience property just for printing out the inspector URL.
   */
  defaultEndpoint: z4.string().optional()
}).optional().default(() => ({
  enabled: defaultEnabled(),
  token: defaultTokenFn
}));

// src/utils/endpoint-parser.ts
function tryParseEndpoint(ctx, options) {
  const { endpoint, path = ["endpoint"], namespace: configNamespace, token: configToken } = options;
  let url;
  try {
    url = new URL(endpoint);
  } catch {
    ctx.addIssue({
      code: "custom",
      message: `invalid URL: ${endpoint}`,
      path
    });
    return void 0;
  }
  if (url.search) {
    ctx.addIssue({
      code: "custom",
      message: "endpoint cannot contain a query string",
      path
    });
    return void 0;
  }
  if (url.hash) {
    ctx.addIssue({
      code: "custom",
      message: "endpoint cannot contain a fragment",
      path
    });
    return void 0;
  }
  const namespace = url.username ? decodeURIComponent(url.username) : void 0;
  const token = url.password ? decodeURIComponent(url.password) : void 0;
  if (token && !namespace) {
    ctx.addIssue({
      code: "custom",
      message: "endpoint cannot have a token without a namespace",
      path
    });
    return void 0;
  }
  if (namespace && configNamespace) {
    ctx.addIssue({
      code: "custom",
      message: "cannot specify namespace both in endpoint URL and as a separate config option",
      path: ["namespace"]
    });
  }
  if (token && configToken) {
    ctx.addIssue({
      code: "custom",
      message: "cannot specify token both in endpoint URL and as a separate config option",
      path: ["token"]
    });
  }
  url.username = "";
  url.password = "";
  const cleanedEndpoint = url.toString();
  return {
    endpoint: cleanedEndpoint,
    namespace,
    token
  };
}

// src/registry/config/driver.ts
import { z as z5 } from "zod/v4";
var DriverConfigSchema = z5.object({
  /** Machine-readable name to identify this driver by. */
  name: z5.string(),
  displayName: z5.string(),
  manager: z5.custom(),
  actor: z5.custom(),
  /**
   * Start actor driver immediately or if this is started separately.
   *
   * For example:
   * - Engine driver needs this to start immediately since this starts the Runner that connects to the engine
   * - Cloudflare Workers should not start it automatically, since the actor only runs in the DO
   * */
  autoStartActorDriver: z5.boolean()
});

// src/registry/config/runner.ts
import { z as z6 } from "zod/v4";
var RunnerConfigSchema = z6.object({
  // MARK: Runner
  totalSlots: z6.number().default(() => getRivetTotalSlots() ?? 1e5),
  runnerName: z6.string().default(() => getRivetRunner() ?? "default"),
  runnerKey: z6.string().optional().transform((x) => x ?? getRivetRunnerKey()),
  version: z6.number().default(() => getRivetRunnerVersion() ?? 1)
});

// src/registry/config/serverless.ts
import { z as z7 } from "zod/v4";
var ConfigureRunnerPoolSchema = z7.object({
  name: z7.string().optional(),
  url: z7.string(),
  headers: z7.record(z7.string(), z7.string()).optional(),
  maxRunners: z7.number().optional(),
  minRunners: z7.number().optional(),
  requestLifespan: z7.number().optional(),
  runnersMargin: z7.number().optional(),
  slotsPerRunner: z7.number().optional(),
  metadata: z7.record(z7.string(), z7.unknown()).optional(),
  metadataPollInterval: z7.number().optional(),
  drainOnVersionUpgrade: z7.boolean().optional()
}).optional();
var ServerlessConfigSchema = z7.object({
  // MARK: Run Engine
  /**
   * @experimental
   *
   * Downloads and starts the full Rust engine process.
   * Auto-enabled in development mode when no endpoint is provided.
   */
  spawnEngine: z7.boolean().default(() => getRivetRunEngine()),
  /** @experimental */
  engineVersion: z7.string().optional().default(() => getRivetRunEngineVersion() ?? VERSION),
  /**
   * @experimental
   *
   * Automatically configure serverless runners in the engine.
   * Can only be used when runnerKind is "serverless".
   * If true, uses default configuration. Can also provide custom configuration.
   */
  configureRunnerPool: ConfigureRunnerPoolSchema.optional(),
  // MARK: Routing
  // TODO: serverlessBasePath? better naming?
  basePath: z7.string().optional().default("/api/rivet"),
  // MARK: Public Endpoint Configuration
  /**
   * The endpoint that clients should connect to.
   *
   * This is useful if clients connect to serverless directly
   * (e.g. `http://localhost:3000/api/rivet`), they will fetch
   * `http://localhost:3000/api/rivet/metadata` and be redirected to
   * the public endpoint.
   *
   * Supports URL auth syntax for namespace and token:
   * - `https://namespace:token@api.rivet.dev`
   * - `https://namespace@api.rivet.dev`
   *
   * Auto-determined based on endpoint and NODE_ENV if not specified.
   *
   * Can also be set via RIVET_PUBLIC_ENDPOINT environment variable.
   */
  publicEndpoint: z7.string().optional().transform((val) => val ?? getRivetPublicEndpoint()),
  /**
   * Token that clients should use when connecting via the public endpoint.
   *
   * Can also be set via RIVET_PUBLIC_TOKEN environment variable.
   *
   * Can also be specified in the publicEndpoint URL as `https://namespace:token@host`.
   */
  publicToken: z7.string().optional().transform((val) => val ?? getRivetPublicToken())
  // There is no publicNamespace config option because the frontend and backend
  // cannot use different namespaces. The namespace is extracted from the
  // publicEndpoint URL auth syntax if provided.
});

// src/registry/config/index.ts
var ActorsSchema = z8.record(
  z8.string(),
  z8.custom()
);
var TestConfigSchema = z8.object({ enabled: z8.boolean() });
var RegistryConfigSchema = z8.object({
  // MARK: Actors
  use: z8.record(z8.string(), z8.custom()),
  // TODO: Find a better way of passing around the test config
  /**
   * Test configuration.
   *
   * DO NOT MANUALLY ENABLE. THIS IS USED INTERNALLY.
   * @internal
   **/
  test: TestConfigSchema.optional().default({ enabled: false }),
  // MARK: Driver
  driver: DriverConfigSchema.optional(),
  // MARK: Networking
  /** @experimental */
  maxIncomingMessageSize: z8.number().optional().default(65536),
  /** @experimental */
  maxOutgoingMessageSize: z8.number().optional().default(1048576),
  // MARK: Runtime
  /**
   * @experimental
   *
   * Disable welcome message.
   * */
  noWelcome: z8.boolean().optional().default(false),
  /**
   * @experimental
   * */
  logging: z8.object({
    baseLogger: z8.custom().optional(),
    level: LogLevelSchema.optional()
  }).optional().default(() => ({})),
  // MARK: Routing
  // // This is a function to allow for lazy configuration of upgradeWebSocket on the
  // // fly. This is required since the dependencies that upgradeWebSocket
  // // (specifically Node.js) can sometimes only be specified after the router is
  // // created or must be imported async using `await import(...)`
  // getUpgradeWebSocket: z.custom<GetUpgradeWebSocket>().optional(),
  // MARK: Runner Configuration
  /**
   * Endpoint to connect to for Rivet Engine.
   *
   * Supports URL auth syntax for namespace and token:
   * - `https://namespace:token@api.rivet.dev`
   * - `https://namespace@api.rivet.dev`
   *
   * Can also be set via RIVET_ENDPOINT environment variables.
   */
  endpoint: z8.string().optional().transform((val) => val ?? getRivetEngine() ?? getRivetEndpoint()),
  token: z8.string().optional().transform((val) => val ?? getRivetToken()),
  namespace: z8.string().optional().transform((val) => val ?? getRivetNamespace()),
  headers: z8.record(z8.string(), z8.string()).optional().default({}),
  // MARK: Client
  // TODO:
  // client: ClientConfigSchema.optional(),
  // MARK: Manager
  /**
   * Whether to start the local manager server.
   * Auto-determined based on endpoint and NODE_ENV if not specified.
   */
  serveManager: z8.boolean().optional(),
  /**
   * @experimental
   *
   * Base path for the manager API. This is used to prefix all routes.
   * For example, if the base path is `/foo`, then the route `/actors`
   * will be available at `/foo/actors`.
   */
  managerBasePath: z8.string().optional().default("/"),
  /**
   * @experimental
   *
   * What port to run the manager on.
   */
  managerPort: z8.number().optional().default(6420),
  /**
   * @experimental
   *
   * What host to bind the manager server to.
   */
  managerHost: z8.string().optional(),
  /** @experimental */
  inspector: InspectorConfigSchema,
  // MARK: Runtime-specific
  serverless: ServerlessConfigSchema.optional().default(
    () => ServerlessConfigSchema.parse({})
  ),
  runner: RunnerConfigSchema.optional().default(
    () => RunnerConfigSchema.parse({})
  )
}).transform((config8, ctx) => {
  const isDevEnv = isDev();
  const parsedEndpoint = config8.endpoint ? tryParseEndpoint(ctx, {
    endpoint: config8.endpoint,
    path: ["endpoint"],
    namespace: config8.namespace,
    token: config8.token
  }) : void 0;
  if (parsedEndpoint && config8.serveManager) {
    ctx.addIssue({
      code: "custom",
      message: "cannot specify both endpoint and serveManager"
    });
  }
  if (config8.serverless.spawnEngine && parsedEndpoint) {
    ctx.addIssue({
      code: "custom",
      message: "cannot specify both spawnEngine and endpoint"
    });
  }
  if (config8.serverless.configureRunnerPool && !parsedEndpoint && !config8.serverless.spawnEngine) {
    ctx.addIssue({
      code: "custom",
      message: "configureRunnerPool requires either endpoint or spawnEngine"
    });
  }
  const endpoint = config8.serverless.spawnEngine ? ENGINE_ENDPOINT : parsedEndpoint == null ? void 0 : parsedEndpoint.endpoint;
  const namespace = (parsedEndpoint == null ? void 0 : parsedEndpoint.namespace) ?? config8.namespace ?? "default";
  const token = (parsedEndpoint == null ? void 0 : parsedEndpoint.token) ?? config8.token;
  const parsedPublicEndpoint = config8.serverless.publicEndpoint ? tryParseEndpoint(ctx, {
    endpoint: config8.serverless.publicEndpoint,
    path: ["serverless", "publicEndpoint"]
  }) : void 0;
  if ((parsedPublicEndpoint == null ? void 0 : parsedPublicEndpoint.namespace) && parsedPublicEndpoint.namespace !== namespace) {
    ctx.addIssue({
      code: "custom",
      message: `publicEndpoint namespace "${parsedPublicEndpoint.namespace}" must match backend namespace "${namespace}"`,
      path: ["serverless", "publicEndpoint"]
    });
  }
  const serveManager = config8.serveManager ?? (isDevEnv && !endpoint);
  const publicEndpoint = (parsedPublicEndpoint == null ? void 0 : parsedPublicEndpoint.endpoint) ?? (isDevEnv && (serveManager || config8.serverless.spawnEngine) ? `http://127.0.0.1:${config8.managerPort}` : void 0);
  const publicNamespace = parsedPublicEndpoint == null ? void 0 : parsedPublicEndpoint.namespace;
  const publicToken = (parsedPublicEndpoint == null ? void 0 : parsedPublicEndpoint.token) ?? config8.serverless.publicToken;
  const willUseEngine = !!endpoint || config8.serverless.spawnEngine;
  const inspector = willUseEngine ? {
    ...config8.inspector,
    enabled: { manager: false, actor: true }
  } : config8.inspector;
  return {
    ...config8,
    endpoint,
    namespace,
    token,
    serveManager,
    publicEndpoint,
    publicNamespace,
    publicToken,
    inspector,
    serverless: {
      ...config8.serverless,
      publicEndpoint
    }
  };
});
function buildActorNames(config8) {
  return Object.fromEntries(
    Object.keys(config8.use).map((actorName) => {
      const definition = config8.use[actorName];
      const options = definition.config.options ?? {};
      const runMeta = getRunMetadata(definition.config.run);
      const metadata = {};
      metadata.icon = options.icon ?? runMeta.icon;
      metadata.name = options.name ?? runMeta.name;
      if (!metadata.icon) delete metadata.icon;
      if (!metadata.name) delete metadata.name;
      return [actorName, { metadata }];
    })
  );
}
var DocInspectorConfigSchema = z8.object({
  enabled: z8.boolean().optional().describe("Whether to enable the Rivet Inspector. Defaults to true in development mode."),
  token: z8.string().optional().describe("Token used to access the Inspector."),
  defaultEndpoint: z8.string().optional().describe("Default RivetKit server endpoint for Rivet Inspector to connect to.")
}).optional().describe("Inspector configuration for debugging and development.");
var DocConfigureRunnerPoolSchema = z8.object({
  name: z8.string().optional().describe("Name of the runner pool."),
  url: z8.string().describe("URL of the serverless platform to configure runners."),
  headers: z8.record(z8.string(), z8.string()).optional().describe("Headers to include in requests to the serverless platform."),
  maxRunners: z8.number().optional().describe("Maximum number of runners in the pool."),
  minRunners: z8.number().optional().describe("Minimum number of runners to keep warm."),
  requestLifespan: z8.number().optional().describe("Maximum lifespan of a request in milliseconds."),
  runnersMargin: z8.number().optional().describe("Buffer margin for scaling runners."),
  slotsPerRunner: z8.number().optional().describe("Number of actor slots per runner."),
  metadata: z8.record(z8.string(), z8.unknown()).optional().describe("Additional metadata to pass to the serverless platform."),
  metadataPollInterval: z8.number().optional().describe("Interval in milliseconds between metadata polls from the engine. Defaults to 10000 milliseconds (10 seconds).")
}).optional();
var DocServerlessConfigSchema = z8.object({
  spawnEngine: z8.boolean().optional().describe("Downloads and starts the full Rust engine process. Auto-enabled in development mode when no endpoint is provided. Default: false"),
  engineVersion: z8.string().optional().describe("Version of the engine to download. Defaults to the current RivetKit version."),
  configureRunnerPool: DocConfigureRunnerPoolSchema.describe("Automatically configure serverless runners in the engine."),
  basePath: z8.string().optional().describe("Base path for serverless API routes. Default: '/api/rivet'"),
  publicEndpoint: z8.string().optional().describe("The endpoint that clients should connect to. Supports URL auth syntax: https://namespace:token@api.rivet.dev"),
  publicToken: z8.string().optional().describe("Token that clients should use when connecting via the public endpoint.")
}).describe("Configuration for serverless deployment mode.");
var DocRunnerConfigSchema = z8.object({
  totalSlots: z8.number().optional().describe("Total number of actor slots available. Default: 100000"),
  runnerName: z8.string().optional().describe("Name of this runner. Default: 'default'"),
  runnerKey: z8.string().optional().describe("Authentication key for the runner."),
  version: z8.number().optional().describe("Version number of this runner. Default: 1")
}).describe("Configuration for runner mode.");
var DocRegistryConfigSchema = z8.object({
  use: z8.record(z8.string(), z8.unknown()).describe("Actor definitions. Keys are actor names, values are actor definitions."),
  maxIncomingMessageSize: z8.number().optional().describe("Maximum size of incoming WebSocket messages in bytes. Default: 65536"),
  maxOutgoingMessageSize: z8.number().optional().describe("Maximum size of outgoing WebSocket messages in bytes. Default: 1048576"),
  noWelcome: z8.boolean().optional().describe("Disable the welcome message on startup. Default: false"),
  logging: z8.object({
    level: LogLevelSchema.optional().describe("Log level for RivetKit. Default: 'warn'")
  }).optional().describe("Logging configuration."),
  endpoint: z8.string().optional().describe("Endpoint URL to connect to Rivet Engine. Supports URL auth syntax: https://namespace:token@api.rivet.dev. Can also be set via RIVET_ENDPOINT environment variable."),
  token: z8.string().optional().describe("Authentication token for Rivet Engine. Can also be set via RIVET_TOKEN environment variable."),
  namespace: z8.string().optional().describe("Namespace to use. Default: 'default'. Can also be set via RIVET_NAMESPACE environment variable."),
  headers: z8.record(z8.string(), z8.string()).optional().describe("Additional headers to include in requests to Rivet Engine."),
  serveManager: z8.boolean().optional().describe("Whether to start the local manager server. Auto-determined based on endpoint and NODE_ENV if not specified."),
  managerBasePath: z8.string().optional().describe("Base path for the manager API. Default: '/'"),
  managerPort: z8.number().optional().describe("Port to run the manager on. Default: 6420"),
  inspector: DocInspectorConfigSchema,
  serverless: DocServerlessConfigSchema.optional(),
  runner: DocRunnerConfigSchema.optional()
}).describe("RivetKit registry configuration.");

// src/common/router.ts
function logger() {
  return getLogger("router");
}
function loggerMiddleware(logger4) {
  return async (c, next) => {
    const method = c.req.method;
    const path = c.req.path;
    const startTime = Date.now();
    await next();
    const duration = Date.now() - startTime;
    logger4.debug({
      msg: "http request",
      method,
      path,
      status: c.res.status,
      dt: `${duration}ms`,
      reqSize: c.req.header("content-length"),
      resSize: c.res.headers.get("content-length"),
      userAgent: c.req.header("user-agent"),
      ...getLogHeaders() ? { allHeaders: JSON.stringify(c.req.header()) } : {}
    });
  };
}
function handleRouteNotFound(c) {
  return c.text("Not Found (RivetKit)", 404);
}
function handleRouteError(error, c) {
  const exposeInternalError = getRequestExposeInternalError(c.req.raw);
  const { statusCode, group, code, message, metadata } = deconstructError(
    error,
    logger(),
    {
      method: c.req.method,
      path: c.req.path
    },
    exposeInternalError
  );
  let encoding;
  try {
    encoding = getRequestEncoding(c.req);
  } catch (_) {
    encoding = "json";
  }
  const errorData = { group, code, message, metadata };
  const output = serializeWithEncoding(
    encoding,
    errorData,
    HTTP_RESPONSE_ERROR_VERSIONED,
    CURRENT_VERSION2,
    HttpResponseErrorSchema,
    // JSON: metadata is the raw value (will be serialized by jsonStringifyCompat)
    (value) => ({
      group: value.group,
      code: value.code,
      message: value.message,
      metadata: value.metadata
    }),
    // BARE/CBOR: metadata needs to be CBOR-encoded to ArrayBuffer
    (value) => ({
      group: value.group,
      code: value.code,
      message: value.message,
      metadata: value.metadata ? bufferToArrayBuffer(cbor3.encode(value.metadata)) : null
    })
  );
  return c.body(output, { status: statusCode });
}
function handleMetadataRequest(c, config8, runnerKind, clientEndpoint, clientNamespace, clientToken) {
  const response = {
    runtime: "rivetkit",
    version: VERSION,
    runner: {
      kind: runnerKind,
      version: config8.runner.version
    },
    actorNames: buildActorNames(config8),
    clientEndpoint,
    clientNamespace,
    clientToken
  };
  return c.json(response);
}
function handleHealthRequest(c) {
  return c.json({
    status: "ok",
    runtime: "rivetkit",
    version: VERSION
  });
}

// src/manager-api/actors.ts
import { z as z10 } from "zod/v4";

// src/manager-api/common.ts
import { z as z9 } from "zod/v4";
var RivetIdSchema = z9.string();

// src/manager-api/actors.ts
var ActorSchema = z10.object({
  actor_id: RivetIdSchema,
  name: z10.string(),
  key: z10.string(),
  namespace_id: RivetIdSchema,
  runner_name_selector: z10.string(),
  create_ts: z10.number(),
  connectable_ts: z10.number().nullable().optional(),
  destroy_ts: z10.number().nullable().optional(),
  sleep_ts: z10.number().nullable().optional(),
  start_ts: z10.number().nullable().optional(),
  error: z10.unknown().nullable().optional()
});
var ActorNameSchema = z10.object({
  metadata: z10.record(z10.string(), z10.unknown())
});
var ActorsListResponseSchema = z10.object({
  actors: z10.array(ActorSchema)
});
var ActorsCreateRequestSchema = z10.object({
  datacenter: z10.string().optional(),
  name: z10.string(),
  runner_name_selector: z10.string(),
  crash_policy: z10.string(),
  key: z10.string().nullable().optional(),
  input: z10.string().nullable().optional()
});
var ActorsCreateResponseSchema = z10.object({
  actor: ActorSchema
});
var ActorsGetOrCreateRequestSchema = z10.object({
  datacenter: z10.string().optional(),
  name: z10.string(),
  key: z10.string(),
  runner_name_selector: z10.string(),
  crash_policy: z10.string(),
  input: z10.string().nullable().optional()
});
var ActorsGetOrCreateResponseSchema = z10.object({
  actor: ActorSchema,
  created: z10.boolean()
});
var ActorsDeleteResponseSchema = z10.object({});
var ActorsListNamesResponseSchema = z10.object({
  names: z10.record(z10.string(), ActorNameSchema)
});
var ActorsKvGetResponseSchema = z10.object({
  value: z10.string().nullable()
});

// src/utils/crypto.ts
function timingSafeEqual(a, b) {
  const encoder = new TextEncoder();
  const bufferA = typeof a === "string" ? encoder.encode(a) : a;
  const bufferB = typeof b === "string" ? encoder.encode(b) : b;
  const maxLength = Math.max(bufferA.byteLength, bufferB.byteLength);
  let result = bufferA.byteLength ^ bufferB.byteLength;
  for (let i = 0; i < maxLength; i++) {
    const byteA = i < bufferA.byteLength ? bufferA[i] : 0;
    const byteB = i < bufferB.byteLength ? bufferB[i] : 0;
    result |= byteA ^ byteB;
  }
  return result === 0;
}

// src/utils/router.ts
import { OpenAPIHono } from "@hono/zod-openapi";
import { createMiddleware } from "hono/factory";

// src/common/cors.ts
var cors = () => {
  return async (c, next) => {
    const origin = c.req.header("origin") || "*";
    if (c.req.method === "OPTIONS") {
      const requestHeaders = c.req.header("access-control-request-headers") || "*";
      c.header("access-control-allow-origin", origin);
      c.header("access-control-allow-credentials", "true");
      c.header(
        "access-control-allow-methods",
        "GET, POST, PUT, DELETE, OPTIONS, PATCH"
      );
      c.header("access-control-allow-headers", requestHeaders);
      c.header("access-control-expose-headers", "*");
      c.header("access-control-max-age", "86400");
      if (origin !== "*") {
        c.header("vary", "Origin");
      }
      c.res.headers.delete("content-length");
      c.res.headers.delete("content-type");
      return c.body(null, 204);
    }
    await next();
    c.header("access-control-allow-origin", origin);
    c.header("access-control-allow-credentials", "true");
    c.header("access-control-expose-headers", "*");
    if (origin !== "*") {
      c.header("vary", "Origin");
    }
  };
};

// src/utils/router.ts
function logger2() {
  return getLogger("router");
}
function createRouter(basePath, builder) {
  const router = new OpenAPIHono({ strict: false }).basePath(basePath);
  router.use("*", loggerMiddleware(logger2()), cors());
  router.use(
    "*",
    createMiddleware(async (c, next) => {
      const upgrade = c.req.header("upgrade");
      const isWebSocket = (upgrade == null ? void 0 : upgrade.toLowerCase()) === "websocket";
      const isGet = c.req.method === "GET";
      if (isGet && isWebSocket) {
        c.header("Sec-WebSocket-Protocol", "rivet");
      }
      await next();
    })
  );
  builder(router);
  router.notFound(handleRouteNotFound);
  router.onError(handleRouteError);
  return { router, openapi: router };
}
function buildOpenApiResponses(schema) {
  return {
    200: {
      description: "Success",
      content: {
        "application/json": {
          schema
        }
      }
    },
    400: {
      description: "User error"
    },
    500: {
      description: "Internal error"
    }
  };
}
function buildOpenApiRequestBody(schema) {
  return {
    required: true,
    content: {
      "application/json": {
        schema
      }
    }
  };
}

// src/manager/gateway.ts
import invariant4 from "invariant";

// src/actor/router-websocket-endpoints.ts
import invariant3 from "invariant";

// src/inspector/handler.ts
import { encodeReadRangeWire } from "@rivetkit/traces/encoding";

// src/inspector/log.ts
function inspectorLogger() {
  return getLogger("inspector");
}

// src/inspector/handler.ts
async function handleWebSocketInspectorConnect({
  actor
}) {
  const inspector = actor.inspector;
  const maxQueueStatusLimit = 200;
  const listeners = [];
  return {
    // NOTE: onOpen cannot be async since this messes up the open event listener order
    onOpen: (_evt, ws) => {
      sendMessage(ws, {
        body: {
          tag: "Init",
          val: {
            connections: inspector.getConnections(),
            rpcs: inspector.getRpcs(),
            state: inspector.isStateEnabled() ? inspector.getState() : null,
            isStateEnabled: inspector.isStateEnabled(),
            isDatabaseEnabled: inspector.isDatabaseEnabled(),
            queueSize: BigInt(inspector.getQueueSize()),
            workflowHistory: inspector.getWorkflowHistory(),
            isWorkflowEnabled: inspector.isWorkflowEnabled()
          }
        }
      });
      listeners.push(
        inspector.emitter.on("stateUpdated", () => {
          sendMessage(ws, {
            body: {
              tag: "StateUpdated",
              val: { state: inspector.getState() }
            }
          });
        }),
        inspector.emitter.on("connectionsUpdated", () => {
          sendMessage(ws, {
            body: {
              tag: "ConnectionsUpdated",
              val: { connections: inspector.getConnections() }
            }
          });
        }),
        inspector.emitter.on("queueUpdated", () => {
          sendMessage(ws, {
            body: {
              tag: "QueueUpdated",
              val: {
                queueSize: BigInt(inspector.getQueueSize())
              }
            }
          });
        }),
        inspector.emitter.on("workflowHistoryUpdated", (history) => {
          sendMessage(ws, {
            body: {
              tag: "WorkflowHistoryUpdated",
              val: { history }
            }
          });
        })
      );
    },
    onMessage: async (evt, ws) => {
      try {
        const message = receiveMessage(evt.data);
        if (message.body.tag === "PatchStateRequest") {
          const { state } = message.body.val;
          inspector.setState(state);
          return;
        } else if (message.body.tag === "ActionRequest") {
          const { name, args, id } = message.body.val;
          const result = await inspector.executeAction(name, args);
          sendMessage(ws, {
            body: {
              tag: "ActionResponse",
              val: {
                rid: id,
                output: result
              }
            }
          });
        } else if (message.body.tag === "StateRequest") {
          sendMessage(ws, {
            body: {
              tag: "StateResponse",
              val: {
                rid: message.body.val.id,
                state: inspector.isStateEnabled() ? inspector.getState() : null,
                isStateEnabled: inspector.isStateEnabled()
              }
            }
          });
        } else if (message.body.tag === "ConnectionsRequest") {
          sendMessage(ws, {
            body: {
              tag: "ConnectionsResponse",
              val: {
                rid: message.body.val.id,
                connections: inspector.getConnections()
              }
            }
          });
        } else if (message.body.tag === "RpcsListRequest") {
          sendMessage(ws, {
            body: {
              tag: "RpcsListResponse",
              val: {
                rid: message.body.val.id,
                rpcs: inspector.getRpcs()
              }
            }
          });
        } else if (message.body.tag === "TraceQueryRequest") {
          const { id, startMs, endMs, limit } = message.body.val;
          const wire = await actor.traces.readRangeWire({
            startMs: Number(startMs),
            endMs: Number(endMs),
            limit: Number(limit)
          });
          sendMessage(ws, {
            body: {
              tag: "TraceQueryResponse",
              val: {
                rid: id,
                payload: bufferToArrayBuffer(
                  encodeReadRangeWire(wire)
                )
              }
            }
          });
        } else if (message.body.tag === "QueueRequest") {
          const { id, limit } = message.body.val;
          const status = await inspector.getQueueStatus(
            Math.min(Number(limit), maxQueueStatusLimit)
          );
          sendMessage(ws, {
            body: {
              tag: "QueueResponse",
              val: {
                rid: id,
                status
              }
            }
          });
        } else if (message.body.tag === "WorkflowHistoryRequest") {
          sendMessage(ws, {
            body: {
              tag: "WorkflowHistoryResponse",
              val: {
                rid: message.body.val.id,
                history: inspector.getWorkflowHistory(),
                isWorkflowEnabled: inspector.isWorkflowEnabled()
              }
            }
          });
        } else {
          assertUnreachable(message.body);
        }
      } catch (error) {
        inspectorLogger().warn(
          { error },
          "Failed to handle inspector WS message"
        );
      }
    },
    onClose: (_event, _ws) => {
      for (const unsubscribe of listeners) {
        unsubscribe();
      }
    },
    onError: (_error) => {
      inspectorLogger().warn(
        { error: _error },
        "WebSocket inspector connection error"
      );
    }
  };
}
function sendMessage(ws, message) {
  ws.send(
    TO_CLIENT_VERSIONED.serializeWithEmbeddedVersion(
      message,
      CURRENT_VERSION
    )
  );
}
function receiveMessage(data) {
  return TO_SERVER_VERSIONED.deserializeWithEmbeddedVersion(new Uint8Array(data));
}

// src/actor/conn/drivers/raw-websocket.ts
function createRawWebSocketDriver(hibernatable, closePromise) {
  let websocket;
  const driver = {
    type: "raw-websocket",
    hibernatable,
    // No sendMessage implementation since this is a raw WebSocket that doesn't
    // handle messages from the RivetKit protocol
    disconnect: async (_actor, _conn, reason) => {
      if (!websocket) {
        loggerWithoutContext().warn(
          "disconnecting raw ws without websocket"
        );
        return;
      }
      websocket.close(1e3, reason);
      await closePromise;
    },
    terminate: () => {
      var _a;
      (_a = websocket == null ? void 0 : websocket.terminate) == null ? void 0 : _a.call(websocket);
    },
    getConnectionReadyState: (_actor, _conn) => {
      return (websocket == null ? void 0 : websocket.readyState) ?? 0 /* CONNECTING */;
    }
  };
  return {
    driver,
    setWebSocket(ws) {
      websocket = ws;
    }
  };
}

// src/actor/conn/drivers/websocket.ts
function createWebSocketDriver(hibernatable, encoding, closePromise, config8) {
  loggerWithoutContext().debug({
    msg: "createWebSocketDriver creating driver",
    hibernatable
  });
  let websocket;
  const driver = {
    type: "websocket",
    hibernatable,
    rivetKitProtocol: {
      sendMessage: (actor, conn, message) => {
        if (!websocket) {
          actor.rLog.warn({
            msg: "websocket not open",
            connId: conn.id
          });
          return;
        }
        if (websocket.readyState !== 1 /* OPEN */) {
          actor.rLog.warn({
            msg: "attempting to send message to closed websocket, this is likely a bug in RivetKit",
            connId: conn.id,
            wsReadyState: websocket.readyState
          });
          return;
        }
        const serialized = message.serialize(encoding);
        actor.rLog.debug({
          msg: "sending websocket message",
          encoding,
          dataType: typeof serialized,
          isUint8Array: serialized instanceof Uint8Array,
          isArrayBuffer: serialized instanceof ArrayBuffer,
          dataLength: serialized.byteLength || serialized.length
        });
        const messageSize = serialized.byteLength || serialized.length;
        if (messageSize > config8.maxOutgoingMessageSize) {
          actor.rLog.error({
            msg: "outgoing message exceeds maxOutgoingMessageSize",
            messageSize,
            maxOutgoingMessageSize: config8.maxOutgoingMessageSize
          });
          throw new OutgoingMessageTooLong();
        }
        if (serialized instanceof Uint8Array) {
          const buffer = serialized.buffer.slice(
            serialized.byteOffset,
            serialized.byteOffset + serialized.byteLength
          );
          if (buffer instanceof SharedArrayBuffer) {
            const arrayBuffer = new ArrayBuffer(buffer.byteLength);
            new Uint8Array(arrayBuffer).set(new Uint8Array(buffer));
            actor.rLog.debug({
              msg: "converted SharedArrayBuffer to ArrayBuffer",
              byteLength: arrayBuffer.byteLength
            });
            websocket.send(arrayBuffer);
          } else {
            actor.rLog.debug({
              msg: "sending ArrayBuffer",
              byteLength: buffer.byteLength
            });
            websocket.send(buffer);
          }
        } else {
          actor.rLog.debug({
            msg: "sending string data",
            length: serialized.length
          });
          websocket.send(serialized);
        }
      }
    },
    disconnect: async (_actor, _conn, reason) => {
      if (!websocket) {
        loggerWithoutContext().warn(
          "disconnecting ws without websocket"
        );
        return;
      }
      websocket.close(1e3, reason);
      await closePromise;
    },
    terminate: () => {
      websocket.terminate();
    },
    getConnectionReadyState: (_actor, _conn) => {
      return (websocket == null ? void 0 : websocket.readyState) ?? 0 /* CONNECTING */;
    }
  };
  return {
    driver,
    setWebSocket(ws) {
      websocket = ws;
    }
  };
}

// src/actor/protocol/old.ts
import * as cbor5 from "cbor-x";

// src/actor/conn/mod.ts
import * as cbor4 from "cbor-x";

// src/actor/conn/state-manager.ts
import onChange from "@rivetkit/on-change";
import invariant2 from "invariant";
var StateManager = class {
  #conn;
  /**
   * Data representing this connection.
   *
   * This is stored as a struct for both ephemeral and hibernatable conns in
   * order to keep the separation clear between the two.
   */
  #data;
  constructor(conn, data) {
    this.#conn = conn;
    if ("ephemeral" in data) {
      this.#data = { ephemeral: { data: data.ephemeral } };
    } else if ("hibernatable" in data) {
      const persistRaw = data.hibernatable;
      const persist = onChange(
        persistRaw,
        (path, value, _previousValue, _applyData) => {
          this.#handleChange(path, value);
        },
        { ignoreDetached: true }
      );
      this.#data = {
        hibernatable: { data: persist, dataRaw: persistRaw }
      };
    } else {
      assertUnreachable2(data);
    }
  }
  /**
   * Returns the ephemeral or persisted data for this connectioned.
   *
   * This property is used to be able to treat both memory & persist conns
   * identical by looking up the correct underlying data structure.
   */
  get ephemeralData() {
    if ("hibernatable" in this.#data) {
      return this.#data.hibernatable.data;
    } else if ("ephemeral" in this.#data) {
      return this.#data.ephemeral.data;
    } else {
      return assertUnreachable2(this.#data);
    }
  }
  get hibernatableData() {
    if ("hibernatable" in this.#data) {
      return this.#data.hibernatable.data;
    } else {
      return void 0;
    }
  }
  hibernatableDataOrError() {
    const hibernatable = this.hibernatableData;
    invariant2(hibernatable, "missing hibernatable data");
    return hibernatable;
  }
  get hibernatableDataRaw() {
    if ("hibernatable" in this.#data) {
      return this.#data.hibernatable.dataRaw;
    } else {
      return void 0;
    }
  }
  get stateEnabled() {
    return this.#conn[CONN_ACTOR_SYMBOL].connStateEnabled;
  }
  get state() {
    this.#validateStateEnabled();
    const state = this.ephemeralData.state;
    if (!state) throw new Error("state should exists");
    return state;
  }
  set state(value) {
    this.#validateStateEnabled();
    this.ephemeralData.state = value;
  }
  #validateStateEnabled() {
    if (!this.#conn[CONN_ACTOR_SYMBOL].connStateEnabled) {
      throw new ConnStateNotEnabled();
    }
  }
  #handleChange(path, value) {
    if (path.startsWith("state")) {
      let invalidPath = "";
      if (!isCborSerializable(
        value,
        (invalidPathPart) => {
          invalidPath = invalidPathPart;
        },
        ""
      )) {
        throw new InvalidStateType({
          path: path + (invalidPath ? `.${invalidPath}` : "")
        });
      }
    }
    this.#conn[CONN_ACTOR_SYMBOL].rLog.debug({
      msg: "conn onChange triggered",
      connId: this.#conn.id,
      path
    });
    this.#conn[CONN_ACTOR_SYMBOL].connectionManager.markConnWithPersistChanged(this.#conn);
  }
  addSubscription({ eventName }) {
    const hibernatable = this.hibernatableData;
    if (!hibernatable) return;
    hibernatable.subscriptions.push({
      eventName
    });
  }
  removeSubscription({ eventName }) {
    const hibernatable = this.hibernatableData;
    if (!hibernatable) return;
    const subIdx = hibernatable.subscriptions.findIndex(
      (s) => s.eventName === eventName
    );
    if (subIdx !== -1) {
      hibernatable.subscriptions.splice(subIdx, 1);
    }
    return subIdx !== -1;
  }
};

// src/actor/conn/mod.ts
var CONN_CONNECTED_SYMBOL = Symbol("connected");
var CONN_SPEAKS_RIVETKIT_SYMBOL = Symbol("speaksRivetKit");
var CONN_DRIVER_SYMBOL = Symbol("driver");
var CONN_ACTOR_SYMBOL = Symbol("actor");
var CONN_STATE_MANAGER_SYMBOL = Symbol("stateManager");
var CONN_SEND_MESSAGE_SYMBOL = Symbol("sendMessage");
var Conn = class {
  #actor;
  get [CONN_ACTOR_SYMBOL]() {
    return this.#actor;
  }
  #stateManager;
  get [CONN_STATE_MANAGER_SYMBOL]() {
    return this.#stateManager;
  }
  /**
   * Connections exist before being connected to an actor. If true, this
   * connection has been connected.
   **/
  [CONN_CONNECTED_SYMBOL] = false;
  /**
   * If undefined, then no socket is connected to this conn
   */
  [CONN_DRIVER_SYMBOL];
  /**
   * If this connection is speaking the RivetKit protocol. If false, this is
   * a raw connection for WebSocket or fetch or inspector.
   **/
  get [CONN_SPEAKS_RIVETKIT_SYMBOL]() {
    var _a;
    return ((_a = this[CONN_DRIVER_SYMBOL]) == null ? void 0 : _a.rivetKitProtocol) !== void 0;
  }
  subscriptions = /* @__PURE__ */ new Set();
  #assertConnected() {
    if (!this[CONN_CONNECTED_SYMBOL])
      throw new InternalError(
        "Connection not connected yet. This happens when trying to use the connection in onBeforeConnect or createConnState."
      );
  }
  // MARK: - Public Getters
  get params() {
    return this.#stateManager.ephemeralData.parameters;
  }
  /**
   * Gets the current state of the connection.
   *
   * Throws an error if the state is not enabled.
   */
  get state() {
    return this.#stateManager.state;
  }
  /**
   * Sets the state of the connection.
   *
   * Throws an error if the state is not enabled.
   */
  set state(value) {
    this.#stateManager.state = value;
  }
  /**
   * Unique identifier for the connection.
   */
  get id() {
    return this.#stateManager.ephemeralData.id;
  }
  /**
   * @experimental
   *
   * If the underlying connection can hibernate.
   */
  get isHibernatable() {
    var _a;
    return ((_a = this[CONN_DRIVER_SYMBOL]) == null ? void 0 : _a.hibernatable) !== void 0;
  }
  /**
   * Initializes a new instance of the Connection class.
   *
   * This should only be constructed by {@link Actor}.
   *
   * @protected
   */
  constructor(actor, data) {
    this.#actor = actor;
    this.#stateManager = new StateManager(this, data);
  }
  /**
   * Sends a raw message to the underlying connection.
   */
  [CONN_SEND_MESSAGE_SYMBOL](message) {
    if (this[CONN_DRIVER_SYMBOL]) {
      const driver = this[CONN_DRIVER_SYMBOL];
      if (driver.rivetKitProtocol) {
        driver.rivetKitProtocol.sendMessage(this.#actor, this, message);
      } else {
        this.#actor.rLog.warn({
          msg: "attempting to send RivetKit protocol message to connection that does not support it",
          conn: this.id
        });
      }
    } else {
      this.#actor.rLog.warn({
        msg: "missing connection driver state for send message",
        conn: this.id
      });
    }
  }
  /**
   * Sends an event with arguments to the client.
   *
   * @param eventName - The name of the event.
   * @param args - The arguments for the event.
   * @see {@link https://rivet.dev/docs/events|Events Documentation}
   */
  send(eventName, ...args) {
    var _a;
    this.#assertConnected();
    if (!this[CONN_SPEAKS_RIVETKIT_SYMBOL]) {
      this.#actor.rLog.warn({
        msg: "cannot send messages to this connection type",
        connId: this.id,
        connType: (_a = this[CONN_DRIVER_SYMBOL]) == null ? void 0 : _a.type
      });
    }
    this.#actor.emitTraceEvent("message.send", {
      "rivet.event.name": eventName,
      "rivet.conn.id": this.id
    });
    const eventData = { name: eventName, args };
    this[CONN_SEND_MESSAGE_SYMBOL](
      new CachedSerializer(
        eventData,
        TO_CLIENT_VERSIONED2,
        CURRENT_VERSION2,
        ToClientSchema,
        // JSON: args is the raw value (array of arguments)
        (value) => ({
          body: {
            tag: "Event",
            val: {
              name: value.name,
              args: value.args
            }
          }
        }),
        // BARE/CBOR: args needs to be CBOR-encoded to ArrayBuffer
        (value) => ({
          body: {
            tag: "Event",
            val: {
              name: value.name,
              args: bufferToArrayBuffer(cbor4.encode(value.args))
            }
          }
        })
      )
    );
  }
  /**
   * Disconnects the client with an optional reason.
   *
   * @param reason - The reason for disconnection.
   */
  async disconnect(reason) {
    if (this[CONN_DRIVER_SYMBOL]) {
      const driver = this[CONN_DRIVER_SYMBOL];
      if (driver.disconnect) {
        driver.disconnect(this.#actor, this, reason);
      } else {
        this.#actor.rLog.debug({
          msg: "no disconnect handler for conn driver",
          conn: this.id
        });
      }
      this.#actor.connectionManager.connDisconnected(this);
    } else {
      this.#actor.rLog.warn({
        msg: "missing connection driver state for disconnect",
        conn: this.id
      });
    }
    this[CONN_DRIVER_SYMBOL] = void 0;
  }
};

// src/actor/protocol/old.ts
function getValueLength(value) {
  if (typeof value === "string") {
    return value.length;
  } else if (value instanceof Blob) {
    return value.size;
  } else if (value instanceof ArrayBuffer || value instanceof SharedArrayBuffer || value instanceof Uint8Array) {
    return value.byteLength;
  } else {
    assertUnreachable(value);
  }
}
async function inputDataToBuffer(data) {
  if (typeof data === "string") {
    return data;
  } else if (data instanceof Blob) {
    const arrayBuffer = await data.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  } else if (data instanceof Uint8Array) {
    return data;
  } else if (data instanceof ArrayBuffer || data instanceof SharedArrayBuffer) {
    return new Uint8Array(data);
  } else {
    throw new MalformedMessage();
  }
}
async function parseMessage(value, opts) {
  const length = getValueLength(value);
  if (length > opts.maxIncomingMessageSize) {
    throw new IncomingMessageTooLong();
  }
  let buffer = await inputDataToBuffer(value);
  if (buffer instanceof Buffer) {
    buffer = new Uint8Array(buffer);
  }
  return deserializeWithEncoding(
    opts.encoding,
    buffer,
    TO_SERVER_VERSIONED2,
    ToServerSchema,
    // JSON: values are already the correct type
    (json) => json,
    // BARE: need to decode ArrayBuffer fields back to unknown
    (bare8) => {
      if (bare8.body.tag === "ActionRequest") {
        return {
          body: {
            tag: "ActionRequest",
            val: {
              id: bare8.body.val.id,
              name: bare8.body.val.name,
              args: cbor5.decode(
                new Uint8Array(bare8.body.val.args)
              )
            }
          }
        };
      } else {
        return bare8;
      }
    }
  );
}
async function processMessage(message, actor, conn, handler) {
  let actionId;
  let actionName;
  try {
    if (message.body.tag === "ActionRequest") {
      if (handler.onExecuteAction === void 0) {
        throw new Unsupported("Action");
      }
      const { id, name, args } = message.body.val;
      actionId = id;
      actionName = name;
      actor.rLog.debug({
        msg: "processing action request",
        actionId: id,
        actionName: name
      });
      const ctx = new ActionContext(actor, conn);
      const output = await handler.onExecuteAction(
        ctx,
        name,
        args
      );
      actor.rLog.debug({
        msg: "sending action response",
        actionId: id,
        actionName: name,
        outputType: typeof output,
        isPromise: output instanceof Promise
      });
      conn[CONN_SEND_MESSAGE_SYMBOL](
        new CachedSerializer(
          output,
          TO_CLIENT_VERSIONED2,
          CURRENT_VERSION2,
          ToClientSchema,
          // JSON: output is the raw value
          (value) => ({
            body: {
              tag: "ActionResponse",
              val: {
                id,
                output: value
              }
            }
          }),
          // BARE/CBOR: output needs to be CBOR-encoded to ArrayBuffer
          (value) => ({
            body: {
              tag: "ActionResponse",
              val: {
                id,
                output: bufferToArrayBuffer(cbor5.encode(value))
              }
            }
          })
        )
      );
      actor.rLog.debug({ msg: "action response sent", id, name });
    } else if (message.body.tag === "SubscriptionRequest") {
      if (handler.onSubscribe === void 0 || handler.onUnsubscribe === void 0) {
        throw new Unsupported("Subscriptions");
      }
      const { eventName, subscribe } = message.body.val;
      actor.rLog.debug({
        msg: "processing subscription request",
        eventName,
        subscribe
      });
      if (subscribe) {
        await handler.onSubscribe(eventName, conn);
      } else {
        await handler.onUnsubscribe(eventName, conn);
      }
      actor.rLog.debug({
        msg: "subscription request completed",
        eventName,
        subscribe
      });
    } else {
      assertUnreachable(message.body);
    }
  } catch (error) {
    const { group, code, message: message2, metadata } = deconstructError(
      error,
      actor.rLog,
      {
        connectionId: conn.id,
        actionId,
        actionName
      },
      getEnvUniversal("RIVET_EXPOSE_ERRORS") === "1" || getEnvUniversal("NODE_ENV") === "development"
    );
    actor.rLog.debug({
      msg: "sending error response",
      actionId,
      actionName,
      code,
      message: message2
    });
    const errorData = { group, code, message: message2, metadata, actionId };
    conn[CONN_SEND_MESSAGE_SYMBOL](
      new CachedSerializer(
        errorData,
        TO_CLIENT_VERSIONED2,
        CURRENT_VERSION2,
        ToClientSchema,
        // JSON: metadata is the raw value (keep as undefined if not present)
        (value) => {
          const val = {
            group: value.group,
            code: value.code,
            message: value.message,
            actionId: value.actionId !== void 0 ? value.actionId : null
          };
          if (value.metadata !== void 0) {
            val.metadata = value.metadata;
          }
          return {
            body: {
              tag: "Error",
              val
            }
          };
        },
        // BARE/CBOR: metadata needs to be CBOR-encoded to ArrayBuffer
        // Note: protocol.Error expects `| null` for optional fields (BARE protocol)
        (value) => ({
          body: {
            tag: "Error",
            val: {
              group: value.group,
              code: value.code,
              message: value.message,
              metadata: value.metadata ? bufferToArrayBuffer(
                cbor5.encode(value.metadata)
              ) : null,
              actionId: value.actionId !== void 0 ? value.actionId : null
            }
          }
        })
      )
    );
    actor.rLog.debug({ msg: "error response sent", actionId, actionName });
  }
}

// src/actor/router-websocket-endpoints.ts
async function routeWebSocket(request, requestPath, requestHeaders, config8, actorDriver, actorId, encoding, parameters, gatewayId, requestId, isHibernatable, isRestoringHibernatable) {
  var _a;
  const exposeInternalError = request ? getRequestExposeInternalError(request) : false;
  let createdConn;
  try {
    const actor = await actorDriver.loadActor(actorId);
    actor.rLog.debug({
      msg: "new websocket connection",
      actorId,
      requestPath,
      isHibernatable
    });
    const closePromiseResolvers = promiseWithResolvers();
    const requestPathWithoutQuery = requestPath.split("?")[0];
    let handler;
    let connDriver;
    if (requestPathWithoutQuery === PATH_CONNECT) {
      const { driver, setWebSocket } = createWebSocketDriver(
        isHibernatable ? { gatewayId, requestId } : void 0,
        encoding,
        closePromiseResolvers.promise,
        config8
      );
      handler = handleWebSocketConnect.bind(void 0, setWebSocket);
      connDriver = driver;
    } else if (requestPathWithoutQuery === PATH_WEBSOCKET_BASE || requestPathWithoutQuery.startsWith(PATH_WEBSOCKET_PREFIX)) {
      const { driver, setWebSocket } = createRawWebSocketDriver(
        isHibernatable ? { gatewayId, requestId } : void 0,
        closePromiseResolvers.promise
      );
      handler = handleRawWebSocket.bind(void 0, setWebSocket);
      connDriver = driver;
    } else if (requestPathWithoutQuery === PATH_INSPECTOR_CONNECT) {
      if (!actor.inspectorToken) {
        throw "WebSocket Inspector Unauthorized: actor does not provide inspector access";
      }
      const inspectorToken = (_a = requestHeaders["sec-websocket-protocol"].split(",").map((p) => p.trim()).find(
        (protocol) => protocol.startsWith(WS_PROTOCOL_INSPECTOR_TOKEN)
      )) == null ? void 0 : _a.split(".")[1];
      if (!inspectorToken || !timingSafeEqual(actor.inspectorToken, inspectorToken)) {
        throw "WebSocket Inspector Unauthorized: invalid token";
      }
      return await handleWebSocketInspectorConnect({ actor });
    } else {
      throw `WebSocket Path Not Found: ${requestPath}`;
    }
    const conn = await actor.connectionManager.prepareConn(
      connDriver,
      parameters,
      request,
      requestPath,
      requestHeaders,
      isHibernatable,
      isRestoringHibernatable
    );
    createdConn = conn;
    return await handler({
      config: config8,
      request,
      encoding,
      actor,
      closePromiseResolvers,
      conn,
      exposeInternalError
    });
  } catch (error) {
    const { group, code } = deconstructError(
      error,
      loggerWithoutContext(),
      {},
      exposeInternalError
    );
    if (createdConn) {
      createdConn.disconnect(`${group}.${code}`);
    }
    return {
      conn: createdConn,
      onOpen: (_evt, ws) => {
        ws.close(1011, `${group}.${code}`);
      },
      onMessage: (_evt, ws) => {
        ws.close(1011, "actor.not_loaded");
      },
      onClose: (_event, _ws) => {
      },
      onError: (_error) => {
      }
    };
  }
}
async function handleWebSocketConnect(setWebSocket, {
  config: runConfig,
  encoding,
  actor,
  closePromiseResolvers,
  conn,
  exposeInternalError
}) {
  return {
    conn,
    actor,
    onRestore: (ws) => {
      setWebSocket(ws);
    },
    // NOTE: onOpen cannot be async since this messes up the open event listener order
    onOpen: (_evt, ws) => {
      actor.rLog.debug("actor websocket open");
      setWebSocket(ws);
      actor.connectionManager.connectConn(conn);
    },
    onMessage: (evt, ws) => {
      actor.rLog.debug({ msg: "received message" });
      const value = evt.data.valueOf();
      parseMessage(value, {
        encoding,
        maxIncomingMessageSize: runConfig.maxIncomingMessageSize
      }).then((message) => {
        actor.processMessage(message, conn).catch((error) => {
          const { group, code } = deconstructError(
            error,
            actor.rLog,
            {
              wsEvent: "message"
            },
            exposeInternalError
          );
          ws.close(1011, `${group}.${code}`);
        });
      }).catch((error) => {
        const { group, code } = deconstructError(
          error,
          actor.rLog,
          {
            wsEvent: "message"
          },
          exposeInternalError
        );
        ws.close(1011, `${group}.${code}`);
      });
    },
    onClose: (event, ws) => {
      closePromiseResolvers.resolve();
      if (event.wasClean) {
        actor.rLog.info({
          msg: "websocket closed",
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });
      } else {
        actor.rLog.warn({
          msg: "websocket closed",
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });
      }
      ws.close(1e3, "hack_force_close");
      conn.disconnect(event == null ? void 0 : event.reason);
    },
    onError: (_error) => {
      try {
        actor.rLog.warn({ msg: "websocket error" });
      } catch (error) {
        deconstructError(
          error,
          actor.rLog,
          { wsEvent: "error" },
          exposeInternalError
        );
      }
    }
  };
}
async function handleRawWebSocket(setWebSocket, { request, actor, closePromiseResolvers, conn }) {
  return {
    conn,
    actor,
    onRestore: (wsContext) => {
      const ws = wsContext.raw;
      invariant3(ws, "missing wsContext.raw");
      setWebSocket(ws);
    },
    // NOTE: onOpen cannot be async since this will cause the client's open
    // event to be called before this completes. Do all async work in
    // handleRawWebSocket root.
    onOpen: (_evt, wsContext) => {
      const ws = wsContext.raw;
      invariant3(ws, "missing wsContext.raw");
      setWebSocket(ws);
      actor.connectionManager.connectConn(conn);
      actor.handleRawWebSocket(conn, ws, request);
    },
    // Raw websocket messages are handled directly by the actor's event
    // listeners on the WebSocket object, not through this callback
    onMessage: (_evt, _ws) => {
    },
    onClose: (evt, ws) => {
      closePromiseResolvers.resolve();
      conn.disconnect(evt == null ? void 0 : evt.reason);
    },
    onError: (error, ws) => {
    }
  };
}
function parseWebSocketProtocols(protocols) {
  let encodingRaw;
  let connParamsRaw;
  if (protocols) {
    const protocolList = protocols.split(",").map((p) => p.trim());
    for (const protocol of protocolList) {
      if (protocol.startsWith(WS_PROTOCOL_ENCODING)) {
        encodingRaw = protocol.substring(WS_PROTOCOL_ENCODING.length);
      } else if (protocol.startsWith(WS_PROTOCOL_CONN_PARAMS)) {
        connParamsRaw = decodeURIComponent(
          protocol.substring(WS_PROTOCOL_CONN_PARAMS.length)
        );
      }
    }
  }
  const encoding = EncodingSchema.parse(encodingRaw ?? "json");
  const connParams = connParamsRaw ? JSON.parse(connParamsRaw) : void 0;
  return { encoding, connParams };
}
function truncateRawWebSocketPathPrefix(path) {
  const url = new URL(path, "http://actor");
  const pathname = url.pathname.replace(/^\/websocket\/?/, "") || "/";
  const normalizedPath = (pathname.startsWith("/") ? pathname : `/${pathname}`) + url.search;
  return normalizedPath;
}

// src/manager/log.ts
function logger3() {
  return getLogger("actor-manager");
}

// src/manager/gateway.ts
async function handleWebSocketGatewayPathBased(config8, managerDriver, c, actorPathInfo, getUpgradeWebSocket) {
  const upgradeWebSocket = getUpgradeWebSocket == null ? void 0 : getUpgradeWebSocket();
  if (!upgradeWebSocket) {
    throw new WebSocketsNotEnabled();
  }
  const { encoding, connParams } = parseWebSocketProtocols(
    c.req.header("sec-websocket-protocol")
  );
  logger3().debug({
    msg: "proxying websocket to actor via path-based routing",
    actorId: actorPathInfo.actorId,
    path: actorPathInfo.remainingPath,
    encoding
  });
  return await managerDriver.proxyWebSocket(
    c,
    actorPathInfo.remainingPath,
    actorPathInfo.actorId,
    encoding,
    // Will be validated by driver
    connParams
  );
}
async function handleHttpGatewayPathBased(managerDriver, c, actorPathInfo) {
  logger3().debug({
    msg: "proxying request to actor via path-based routing",
    actorId: actorPathInfo.actorId,
    path: actorPathInfo.remainingPath,
    method: c.req.method
  });
  const proxyHeaders = new Headers(c.req.raw.headers);
  const proxyUrl = new URL(`http://actor${actorPathInfo.remainingPath}`);
  const proxyRequest = new Request(proxyUrl, {
    method: c.req.raw.method,
    headers: proxyHeaders,
    body: c.req.raw.body,
    signal: c.req.raw.signal,
    duplex: "half"
  });
  return await managerDriver.proxyRequest(
    c,
    proxyRequest,
    actorPathInfo.actorId
  );
}
async function actorGateway(config8, managerDriver, getUpgradeWebSocket, c, next) {
  if (c.req.path.startsWith("/.test/")) {
    return next();
  }
  let strippedPath = c.req.path;
  if (config8.managerBasePath && strippedPath.startsWith(config8.managerBasePath)) {
    strippedPath = strippedPath.slice(config8.managerBasePath.length);
    if (!strippedPath.startsWith("/")) {
      strippedPath = "/" + strippedPath;
    }
  }
  const pathWithQuery = c.req.url.includes("?") ? strippedPath + c.req.url.substring(c.req.url.indexOf("?")) : strippedPath;
  const actorPathInfo = parseActorPath(pathWithQuery);
  if (actorPathInfo) {
    logger3().debug({
      msg: "routing using path-based actor routing",
      actorPathInfo
    });
    const isWebSocket = c.req.header("upgrade") === "websocket";
    if (isWebSocket) {
      return await handleWebSocketGatewayPathBased(
        config8,
        managerDriver,
        c,
        actorPathInfo,
        getUpgradeWebSocket
      );
    }
    return await handleHttpGatewayPathBased(
      managerDriver,
      c,
      actorPathInfo
    );
  }
  if (c.req.header("upgrade") === "websocket") {
    return await handleWebSocketGateway(
      config8,
      managerDriver,
      getUpgradeWebSocket,
      c,
      strippedPath
    );
  }
  return await handleHttpGateway(managerDriver, c, next, strippedPath);
}
async function handleWebSocketGateway(config8, managerDriver, getUpgradeWebSocket, c, strippedPath) {
  const upgradeWebSocket = getUpgradeWebSocket == null ? void 0 : getUpgradeWebSocket();
  if (!upgradeWebSocket) {
    throw new WebSocketsNotEnabled();
  }
  let target;
  let actorId;
  const { encoding, connParams } = parseWebSocketProtocols(
    c.req.header("sec-websocket-protocol")
  );
  if (target !== "actor") {
    return c.text("WebSocket upgrade requires target.actor protocol", 400);
  }
  if (!actorId) {
    throw new MissingActorHeader();
  }
  logger3().debug({
    msg: "proxying websocket to actor",
    actorId,
    path: strippedPath,
    encoding
  });
  const pathWithQuery = c.req.url.includes("?") ? strippedPath + c.req.url.substring(c.req.url.indexOf("?")) : strippedPath;
  return await managerDriver.proxyWebSocket(
    c,
    pathWithQuery,
    actorId,
    encoding,
    connParams
  );
}
async function handleHttpGateway(managerDriver, c, next, strippedPath) {
  const target = c.req.header(HEADER_RIVET_TARGET);
  const actorId = c.req.header(HEADER_RIVET_ACTOR);
  if (target !== "actor") {
    return next();
  }
  if (!actorId) {
    throw new MissingActorHeader();
  }
  logger3().debug({
    msg: "proxying request to actor",
    actorId,
    path: strippedPath,
    method: c.req.method
  });
  const proxyHeaders = new Headers(c.req.raw.headers);
  proxyHeaders.delete(HEADER_RIVET_TARGET);
  proxyHeaders.delete(HEADER_RIVET_ACTOR);
  const url = new URL(c.req.url);
  const proxyUrl = new URL(`http://actor${strippedPath}${url.search}`);
  const proxyRequest = new Request(proxyUrl, {
    method: c.req.raw.method,
    headers: proxyHeaders,
    body: c.req.raw.body,
    signal: c.req.raw.signal,
    duplex: "half"
  });
  return await managerDriver.proxyRequest(c, proxyRequest, actorId);
}
function parseActorPath(path) {
  const queryPos = path.indexOf("?");
  const fragmentPos = path.indexOf("#");
  let queryString = "";
  if (queryPos !== -1) {
    if (fragmentPos !== -1 && queryPos < fragmentPos) {
      queryString = path.slice(queryPos, fragmentPos);
    } else {
      queryString = path.slice(queryPos);
    }
  }
  let basePath = path;
  if (queryPos !== -1) {
    basePath = path.slice(0, queryPos);
  } else if (fragmentPos !== -1) {
    basePath = path.slice(0, fragmentPos);
  }
  if (basePath.includes("//")) {
    return null;
  }
  const segments = basePath.split("/").filter((s) => s.length > 0);
  if (segments.length < 2) {
    return null;
  }
  if (segments[0] !== "gateway") {
    return null;
  }
  const actorSegment = segments[1];
  if (actorSegment.length === 0) {
    return null;
  }
  let actorId;
  let token;
  const atPos = actorSegment.indexOf("@");
  if (atPos !== -1) {
    const rawActorId = actorSegment.slice(0, atPos);
    const rawToken = actorSegment.slice(atPos + 1);
    if (rawActorId.length === 0 || rawToken.length === 0) {
      return null;
    }
    try {
      actorId = decodeURIComponent(rawActorId);
      token = decodeURIComponent(rawToken);
    } catch (e) {
      return null;
    }
  } else {
    try {
      actorId = decodeURIComponent(actorSegment);
    } catch (e) {
      return null;
    }
    token = void 0;
  }
  let prefixLen = 0;
  for (let i = 0; i < 2; i++) {
    prefixLen += 1 + segments[i].length;
  }
  let remainingBase;
  if (prefixLen < basePath.length) {
    remainingBase = basePath.slice(prefixLen);
  } else {
    remainingBase = "/";
  }
  let remainingPath;
  if (remainingBase.length === 0 || !remainingBase.startsWith("/")) {
    remainingPath = `/${remainingBase}${queryString}`;
  } else {
    remainingPath = `${remainingBase}${queryString}`;
  }
  return {
    actorId,
    token,
    remainingPath
  };
}
async function createTestWebSocketProxy(proxyToActorWsPromise) {
  let proxyToActorWs = null;
  const {
    promise: clientToProxyWsPromise,
    resolve: clientToProxyWsResolve,
    reject: clientToProxyWsReject
  } = promiseWithResolvers();
  try {
    logger3().debug({ msg: "awaiting client websocket promise" });
    proxyToActorWs = await proxyToActorWsPromise;
    logger3().debug({
      msg: "client websocket promise resolved",
      constructor: proxyToActorWs == null ? void 0 : proxyToActorWs.constructor.name
    });
    await new Promise((resolve, reject) => {
      invariant4(proxyToActorWs, "missing proxyToActorWs");
      const onOpen = () => {
        logger3().debug({
          msg: "test websocket connection to actor opened"
        });
        resolve();
      };
      const onError = (error) => {
        logger3().error({
          msg: "test websocket connection failed",
          error
        });
        reject(
          new Error(
            `Failed to open WebSocket: ${error.message || error}`
          )
        );
        clientToProxyWsReject();
      };
      proxyToActorWs.addEventListener("open", onOpen);
      proxyToActorWs.addEventListener("error", onError);
      proxyToActorWs.addEventListener(
        "message",
        async (clientEvt) => {
          var _a, _b;
          const clientToProxyWs = await clientToProxyWsPromise;
          logger3().debug({
            msg: `test websocket connection message from client`,
            dataType: typeof clientEvt.data,
            isBlob: clientEvt.data instanceof Blob,
            isArrayBuffer: clientEvt.data instanceof ArrayBuffer,
            dataConstructor: (_b = (_a = clientEvt.data) == null ? void 0 : _a.constructor) == null ? void 0 : _b.name,
            dataStr: typeof clientEvt.data === "string" ? clientEvt.data.substring(0, 100) : void 0
          });
          if (clientToProxyWs.readyState === 1) {
            if (clientEvt.data instanceof Blob) {
              clientEvt.data.arrayBuffer().then((buffer) => {
                logger3().debug({
                  msg: "converted client blob to arraybuffer, sending to server",
                  bufferSize: buffer.byteLength
                });
                clientToProxyWs.send(buffer);
              }).catch((error) => {
                logger3().error({
                  msg: "failed to convert blob to arraybuffer",
                  error
                });
              });
            } else {
              logger3().debug({
                msg: "sending client data directly to server",
                dataType: typeof clientEvt.data,
                dataLength: typeof clientEvt.data === "string" ? clientEvt.data.length : void 0
              });
              clientToProxyWs.send(clientEvt.data);
            }
          }
        }
      );
      proxyToActorWs.addEventListener("close", async (clientEvt) => {
        const clientToProxyWs = await clientToProxyWsPromise;
        logger3().debug({
          msg: `test websocket connection closed`
        });
        if (clientToProxyWs.readyState !== 3) {
          clientToProxyWs.close(clientEvt.code, clientEvt.reason);
        }
      });
      proxyToActorWs.addEventListener("error", async () => {
        const clientToProxyWs = await clientToProxyWsPromise;
        logger3().debug({
          msg: `test websocket connection error`
        });
        if (clientToProxyWs.readyState !== 3) {
          clientToProxyWs.close(1011, "Error in client websocket");
        }
      });
    });
  } catch (error) {
    logger3().error({
      msg: `failed to establish client websocket connection`,
      error
    });
    return {
      onOpen: (_evt, clientToProxyWs) => {
        clientToProxyWs.close(1011, "Failed to establish connection");
      },
      onMessage: () => {
      },
      onError: () => {
      },
      onClose: () => {
      }
    };
  }
  return {
    onOpen: (_evt, clientToProxyWs) => {
      logger3().debug({
        msg: `test websocket connection from client opened`
      });
      logger3().debug({
        msg: "proxyToActorWs info",
        constructor: proxyToActorWs.constructor.name,
        hasAddEventListener: typeof proxyToActorWs.addEventListener === "function",
        readyState: proxyToActorWs.readyState
      });
      clientToProxyWsResolve(clientToProxyWs);
    },
    onMessage: (evt) => {
      var _a, _b;
      logger3().debug({
        msg: "received message from server",
        dataType: typeof evt.data,
        isBlob: evt.data instanceof Blob,
        isArrayBuffer: evt.data instanceof ArrayBuffer,
        dataConstructor: (_b = (_a = evt.data) == null ? void 0 : _a.constructor) == null ? void 0 : _b.name,
        dataStr: typeof evt.data === "string" ? evt.data.substring(0, 100) : void 0
      });
      if (proxyToActorWs.readyState === 1) {
        if (evt.data instanceof Blob) {
          evt.data.arrayBuffer().then((buffer) => {
            logger3().debug({
              msg: "converted blob to arraybuffer, sending",
              bufferSize: buffer.byteLength
            });
            proxyToActorWs.send(buffer);
          }).catch((error) => {
            logger3().error({
              msg: "failed to convert blob to arraybuffer",
              error
            });
          });
        } else {
          logger3().debug({
            msg: "sending data directly",
            dataType: typeof evt.data,
            dataLength: typeof evt.data === "string" ? evt.data.length : void 0
          });
          proxyToActorWs.send(evt.data);
        }
      }
    },
    onClose: (event, clientToProxyWs) => {
      logger3().debug({
        msg: `server websocket closed`,
        wasClean: event.wasClean,
        code: event.code,
        reason: event.reason
      });
      clientToProxyWs.close(1e3, "hack_force_close");
      if (proxyToActorWs && proxyToActorWs.readyState !== proxyToActorWs.CLOSED && proxyToActorWs.readyState !== proxyToActorWs.CLOSING) {
        proxyToActorWs.close(1e3, event.reason);
      }
    },
    onError: (error) => {
      logger3().error({
        msg: `error in server websocket`,
        error
      });
      if (proxyToActorWs && proxyToActorWs.readyState !== proxyToActorWs.CLOSED && proxyToActorWs.readyState !== proxyToActorWs.CLOSING) {
        proxyToActorWs.close(1011, "Error in server websocket");
      }
      clientToProxyWsReject();
    }
  };
}

// src/manager/router.ts
function buildManagerRouter(config8, managerDriver, getUpgradeWebSocket) {
  return createRouter(config8.managerBasePath, (router) => {
    var _a;
    router.use(
      "*",
      actorGateway.bind(
        void 0,
        config8,
        managerDriver,
        getUpgradeWebSocket
      )
    );
    router.get("/", (c) => {
      return c.text(
        "This is a RivetKit server.\n\nLearn more at https://rivet.dev"
      );
    });
    {
      const route = createRoute({
        method: "get",
        path: "/actors",
        request: {
          query: z11.object({
            name: z11.string().optional(),
            actor_ids: z11.string().optional(),
            key: z11.string().optional()
          })
        },
        responses: buildOpenApiResponses(ActorsListResponseSchema)
      });
      router.openapi(route, async (c) => {
        const { name, actor_ids, key } = c.req.valid("query");
        const actorIdsParsed = actor_ids ? actor_ids.split(",").map((id) => id.trim()).filter((id) => id.length > 0) : void 0;
        const actors = [];
        if (actorIdsParsed && (name || key)) {
          return c.json(
            {
              error: "Cannot provide both actor_ids and (name + key). Use either actor_ids or (name + key)."
            },
            400
          );
        }
        if (key && !name) {
          return c.json(
            {
              error: "Name is required when key is provided."
            },
            400
          );
        }
        if (actorIdsParsed) {
          if (actorIdsParsed.length > 32) {
            return c.json(
              {
                error: `Too many actor IDs. Maximum is 32, got ${actorIdsParsed.length}.`
              },
              400
            );
          }
          if (actorIdsParsed.length === 0) {
            return c.json({
              actors: []
            });
          }
          for (const actorId of actorIdsParsed) {
            if (name) {
              const actorOutput = await managerDriver.getForId({
                c,
                name,
                actorId
              });
              if (actorOutput) {
                actors.push(actorOutput);
              }
            } else {
              for (const actorName of Object.keys(config8.use)) {
                const actorOutput = await managerDriver.getForId({
                  c,
                  name: actorName,
                  actorId
                });
                if (actorOutput) {
                  actors.push(actorOutput);
                  break;
                }
              }
            }
          }
        } else if (key && name) {
          const actorOutput = await managerDriver.getWithKey({
            c,
            name,
            key: [key]
            // Convert string to ActorKey array
          });
          if (actorOutput) {
            actors.push(actorOutput);
          }
        } else {
          if (!name) {
            return c.json(
              {
                error: "Name is required when not using actor_ids."
              },
              400
            );
          }
          const actorOutputs = await managerDriver.listActors({
            c,
            name,
            key,
            includeDestroyed: false
          });
          actors.push(...actorOutputs);
        }
        return c.json({
          actors: actors.map((actor) => createApiActor(actor))
        });
      });
    }
    {
      const route = createRoute({
        method: "get",
        path: "/actors/names",
        request: {
          query: z11.object({
            namespace: z11.string()
          })
        },
        responses: buildOpenApiResponses(ActorsListNamesResponseSchema)
      });
      router.openapi(route, async (c) => {
        const names = buildActorNames(config8);
        return c.json({
          names
        });
      });
    }
    {
      const route = createRoute({
        method: "put",
        path: "/actors",
        request: {
          body: buildOpenApiRequestBody(
            ActorsGetOrCreateRequestSchema
          )
        },
        responses: buildOpenApiResponses(
          ActorsGetOrCreateResponseSchema
        )
      });
      router.openapi(route, async (c) => {
        const body = c.req.valid("json");
        const existingActor = await managerDriver.getWithKey({
          c,
          name: body.name,
          key: [body.key]
          // Convert string to ActorKey array
        });
        if (existingActor) {
          return c.json({
            actor: createApiActor(existingActor),
            created: false
          });
        }
        const newActor = await managerDriver.getOrCreateWithKey({
          c,
          name: body.name,
          key: [body.key],
          // Convert string to ActorKey array
          input: body.input ? cbor6.decode(Buffer.from(body.input, "base64")) : void 0,
          region: void 0
          // Not provided in the request schema
        });
        return c.json({
          actor: createApiActor(newActor),
          created: true
        });
      });
    }
    {
      const route = createRoute({
        method: "post",
        path: "/actors",
        request: {
          body: buildOpenApiRequestBody(ActorsCreateRequestSchema)
        },
        responses: buildOpenApiResponses(ActorsCreateResponseSchema)
      });
      router.openapi(route, async (c) => {
        const body = c.req.valid("json");
        const actorOutput = await managerDriver.createActor({
          c,
          name: body.name,
          key: [body.key || crypto.randomUUID()],
          // Generate key if not provided, convert to ActorKey array
          input: body.input ? cbor6.decode(Buffer.from(body.input, "base64")) : void 0,
          region: void 0
          // Not provided in the request schema
        });
        const actor = createApiActor(actorOutput);
        return c.json({ actor });
      });
    }
    {
      const route = createRoute({
        method: "get",
        path: "/actors/{actor_id}/kv/keys/{key}",
        request: {
          params: z11.object({
            actor_id: z11.string(),
            key: z11.string()
          })
        },
        responses: buildOpenApiResponses(ActorsKvGetResponseSchema)
      });
      router.openapi(route, async (c) => {
        if (getNodeEnv() === "development" && !config8.token) {
          logger3().warn({
            msg: "RIVET_TOKEN is not set, skipping KV store access checks in development mode. This endpoint will be disabled in production, unless you set the token."
          });
        }
        if (getNodeEnv() !== "development") {
          if (!config8.token) {
            throw new RestrictedFeature("KV store access");
          }
          if (timingSafeEqual(
            config8.token,
            c.req.header(HEADER_RIVET_TOKEN) || ""
          ) === false) {
            throw new Forbidden();
          }
        }
        const { actor_id: actorId, key } = c.req.valid("param");
        const response = await managerDriver.kvGet(
          actorId,
          Buffer.from(key, "base64")
        );
        return c.json({
          value: response ? Buffer.from(response).toString("base64") : null
        });
      });
    }
    if (config8.test.enabled) {
      router.post(".test/inline-driver/call", async (c) => {
        const buffer = await c.req.arrayBuffer();
        const { encoding, method, args } = cbor6.decode(new Uint8Array(buffer));
        logger3().debug({
          msg: "received inline request",
          encoding,
          method,
          args
        });
        let response;
        try {
          const output = await managerDriver[method](...args);
          response = { ok: output };
        } catch (rawErr) {
          const err = deconstructError(rawErr, logger3(), {}, true);
          response = { err };
        }
        return c.body(cbor6.encode(response));
      });
      router.get(".test/inline-driver/connect-websocket/*", async (c) => {
        const upgradeWebSocket = getUpgradeWebSocket == null ? void 0 : getUpgradeWebSocket();
        invariant5(
          upgradeWebSocket,
          "websockets not supported on this platform"
        );
        return upgradeWebSocket(async (c2) => {
          const protocolHeader = c2.req.header("sec-websocket-protocol") || "";
          const protocols = protocolHeader.split(/,\s*/);
          let actorId = "";
          let encoding = "bare";
          let path = "";
          let params;
          for (const protocol of protocols) {
            if (protocol.startsWith(WS_PROTOCOL_ACTOR)) {
              actorId = decodeURIComponent(
                protocol.substring(WS_PROTOCOL_ACTOR.length)
              );
            } else if (protocol.startsWith(WS_PROTOCOL_ENCODING)) {
              encoding = protocol.substring(
                WS_PROTOCOL_ENCODING.length
              );
            } else if (protocol.startsWith(WS_TEST_PROTOCOL_PATH)) {
              path = decodeURIComponent(
                protocol.substring(
                  WS_TEST_PROTOCOL_PATH.length
                )
              );
            } else if (protocol.startsWith(WS_PROTOCOL_CONN_PARAMS)) {
              const paramsRaw = decodeURIComponent(
                protocol.substring(
                  WS_PROTOCOL_CONN_PARAMS.length
                )
              );
              params = JSON.parse(paramsRaw);
            }
          }
          logger3().debug({
            msg: "received test inline driver websocket",
            actorId,
            params,
            encodingKind: encoding,
            path
          });
          const clientToProxyWsPromise = managerDriver.openWebSocket(
            path,
            actorId,
            encoding,
            params
          );
          return await createTestWebSocketProxy(
            clientToProxyWsPromise
          );
        })(c, noopNext());
      });
      router.all(".test/inline-driver/send-request/*", async (c) => {
        const actorId = c.req.header(HEADER_ACTOR_ID);
        if (!actorId) {
          return c.text("Missing required headers", 400);
        }
        const pathOnly = c.req.path.split("/.test/inline-driver/send-request/")[1] || "";
        const url = new URL(c.req.url);
        const pathWithQuery = pathOnly + url.search;
        logger3().debug({
          msg: "received test inline driver raw http",
          actorId,
          path: pathWithQuery,
          method: c.req.method
        });
        try {
          const response = await managerDriver.sendRequest(
            actorId,
            new Request(`http://actor/${pathWithQuery}`, {
              method: c.req.method,
              headers: c.req.raw.headers,
              body: c.req.raw.body,
              duplex: "half"
            })
          );
          return response;
        } catch (error) {
          logger3().error({
            msg: "error in test inline raw http",
            error: stringifyError(error)
          });
          const err = deconstructError(error, logger3(), {}, true);
          return c.json(
            {
              error: {
                code: err.code,
                message: err.message,
                metadata: err.metadata
              }
            },
            err.statusCode
          );
        }
      });
      router.post("/.test/force-disconnect", async (c) => {
        const actorId = c.req.query("actor");
        const connId = c.req.query("conn");
        if (!actorId || !connId) {
          return c.text(
            "Missing actor or conn query parameters",
            400
          );
        }
        logger3().debug({
          msg: "forcing unclean disconnect",
          actorId,
          connId
        });
        try {
          const response = await managerDriver.sendRequest(
            actorId,
            new Request(
              `http://actor/.test/force-disconnect?conn=${connId}`,
              {
                method: "POST"
              }
            )
          );
          if (!response.ok) {
            const text = await response.text();
            return c.text(
              `Failed to force disconnect: ${text}`,
              response.status
            );
          }
          return c.json({ success: true });
        } catch (error) {
          logger3().error({
            msg: "error forcing disconnect",
            error: stringifyError(error)
          });
          return c.text(`Error: ${error}`, 500);
        }
      });
    }
    router.get("/health", (c) => handleHealthRequest(c));
    router.get(
      "/metadata",
      (c) => handleMetadataRequest(
        c,
        config8,
        { normal: {} },
        config8.publicEndpoint,
        config8.publicNamespace,
        config8.publicToken
      )
    );
    (_a = managerDriver.modifyManagerRouter) == null ? void 0 : _a.call(managerDriver, config8, router);
  });
}
function createApiActor(actor) {
  return {
    actor_id: actor.actorId,
    name: actor.name,
    key: serializeActorKey(actor.key),
    namespace_id: "default",
    // Assert default namespace
    runner_name_selector: "default",
    create_ts: actor.createTs ?? Date.now(),
    connectable_ts: actor.connectableTs ?? null,
    destroy_ts: actor.destroyTs ?? null,
    sleep_ts: actor.sleepTs ?? null,
    start_ts: actor.startTs ?? null
  };
}

// src/driver-helpers/utils.ts
import * as cbor7 from "cbor-x";

// src/schemas/actor-persist/versioned.ts
import { createVersionedDataHandler as createVersionedDataHandler2 } from "vbare";

// dist/schemas/actor-persist/v1.ts
import * as bare4 from "@rivetkit/bare-ts";
var config4 = /* @__PURE__ */ bare4.Config({});
function readPersistedSubscription(bc) {
  return {
    eventName: bare4.readString(bc)
  };
}
function writePersistedSubscription(bc, x) {
  bare4.writeString(bc, x.eventName);
}
function read04(bc) {
  const len = bare4.readUintSafe(bc);
  if (len === 0) {
    return [];
  }
  const result = [readPersistedSubscription(bc)];
  for (let i = 1; i < len; i++) {
    result[i] = readPersistedSubscription(bc);
  }
  return result;
}
function write04(bc, x) {
  bare4.writeUintSafe(bc, x.length);
  for (let i = 0; i < x.length; i++) {
    writePersistedSubscription(bc, x[i]);
  }
}
function readPersistedConnection(bc) {
  return {
    id: bare4.readString(bc),
    token: bare4.readString(bc),
    parameters: bare4.readData(bc),
    state: bare4.readData(bc),
    subscriptions: read04(bc),
    lastSeen: bare4.readU64(bc)
  };
}
function writePersistedConnection(bc, x) {
  bare4.writeString(bc, x.id);
  bare4.writeString(bc, x.token);
  bare4.writeData(bc, x.parameters);
  bare4.writeData(bc, x.state);
  write04(bc, x.subscriptions);
  bare4.writeU64(bc, x.lastSeen);
}
function read14(bc) {
  return bare4.readBool(bc) ? bare4.readData(bc) : null;
}
function write14(bc, x) {
  bare4.writeBool(bc, x !== null);
  if (x !== null) {
    bare4.writeData(bc, x);
  }
}
function readGenericPersistedScheduleEvent(bc) {
  return {
    action: bare4.readString(bc),
    args: read14(bc)
  };
}
function writeGenericPersistedScheduleEvent(bc, x) {
  bare4.writeString(bc, x.action);
  write14(bc, x.args);
}
function readPersistedScheduleEventKind(bc) {
  const offset = bc.offset;
  const tag = bare4.readU8(bc);
  switch (tag) {
    case 0:
      return { tag: "GenericPersistedScheduleEvent", val: readGenericPersistedScheduleEvent(bc) };
    default: {
      bc.offset = offset;
      throw new bare4.BareError(offset, "invalid tag");
    }
  }
}
function writePersistedScheduleEventKind(bc, x) {
  switch (x.tag) {
    case "GenericPersistedScheduleEvent": {
      bare4.writeU8(bc, 0);
      writeGenericPersistedScheduleEvent(bc, x.val);
      break;
    }
  }
}
function readPersistedScheduleEvent(bc) {
  return {
    eventId: bare4.readString(bc),
    timestamp: bare4.readU64(bc),
    kind: readPersistedScheduleEventKind(bc)
  };
}
function writePersistedScheduleEvent(bc, x) {
  bare4.writeString(bc, x.eventId);
  bare4.writeU64(bc, x.timestamp);
  writePersistedScheduleEventKind(bc, x.kind);
}
function read22(bc) {
  const len = bare4.readUintSafe(bc);
  if (len === 0) {
    return [];
  }
  const result = [readPersistedConnection(bc)];
  for (let i = 1; i < len; i++) {
    result[i] = readPersistedConnection(bc);
  }
  return result;
}
function write22(bc, x) {
  bare4.writeUintSafe(bc, x.length);
  for (let i = 0; i < x.length; i++) {
    writePersistedConnection(bc, x[i]);
  }
}
function read32(bc) {
  const len = bare4.readUintSafe(bc);
  if (len === 0) {
    return [];
  }
  const result = [readPersistedScheduleEvent(bc)];
  for (let i = 1; i < len; i++) {
    result[i] = readPersistedScheduleEvent(bc);
  }
  return result;
}
function write32(bc, x) {
  bare4.writeUintSafe(bc, x.length);
  for (let i = 0; i < x.length; i++) {
    writePersistedScheduleEvent(bc, x[i]);
  }
}
function readPersistedActor(bc) {
  return {
    input: read14(bc),
    hasInitialized: bare4.readBool(bc),
    state: bare4.readData(bc),
    connections: read22(bc),
    scheduledEvents: read32(bc)
  };
}
function writePersistedActor(bc, x) {
  write14(bc, x.input);
  bare4.writeBool(bc, x.hasInitialized);
  bare4.writeData(bc, x.state);
  write22(bc, x.connections);
  write32(bc, x.scheduledEvents);
}
function encodePersistedActor(x) {
  const bc = new bare4.ByteCursor(
    new Uint8Array(config4.initialBufferLength),
    config4
  );
  writePersistedActor(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodePersistedActor(bytes) {
  const bc = new bare4.ByteCursor(bytes, config4);
  const result = readPersistedActor(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare4.BareError(bc.offset, "remaining bytes");
  }
  return result;
}

// dist/schemas/actor-persist/v2.ts
import * as bare5 from "@rivetkit/bare-ts";
var config5 = /* @__PURE__ */ bare5.Config({});
function readPersistedSubscription2(bc) {
  return {
    eventName: bare5.readString(bc)
  };
}
function writePersistedSubscription2(bc, x) {
  bare5.writeString(bc, x.eventName);
}
function read05(bc) {
  const len = bare5.readUintSafe(bc);
  if (len === 0) {
    return [];
  }
  const result = [readPersistedSubscription2(bc)];
  for (let i = 1; i < len; i++) {
    result[i] = readPersistedSubscription2(bc);
  }
  return result;
}
function write05(bc, x) {
  bare5.writeUintSafe(bc, x.length);
  for (let i = 0; i < x.length; i++) {
    writePersistedSubscription2(bc, x[i]);
  }
}
function read15(bc) {
  return bare5.readBool(bc) ? bare5.readData(bc) : null;
}
function write15(bc, x) {
  bare5.writeBool(bc, x !== null);
  if (x !== null) {
    bare5.writeData(bc, x);
  }
}
function readPersistedConnection2(bc) {
  return {
    id: bare5.readString(bc),
    token: bare5.readString(bc),
    parameters: bare5.readData(bc),
    state: bare5.readData(bc),
    subscriptions: read05(bc),
    lastSeen: bare5.readI64(bc),
    hibernatableRequestId: read15(bc)
  };
}
function writePersistedConnection2(bc, x) {
  bare5.writeString(bc, x.id);
  bare5.writeString(bc, x.token);
  bare5.writeData(bc, x.parameters);
  bare5.writeData(bc, x.state);
  write05(bc, x.subscriptions);
  bare5.writeI64(bc, x.lastSeen);
  write15(bc, x.hibernatableRequestId);
}
function readGenericPersistedScheduleEvent2(bc) {
  return {
    action: bare5.readString(bc),
    args: read15(bc)
  };
}
function writeGenericPersistedScheduleEvent2(bc, x) {
  bare5.writeString(bc, x.action);
  write15(bc, x.args);
}
function readPersistedScheduleEventKind2(bc) {
  const offset = bc.offset;
  const tag = bare5.readU8(bc);
  switch (tag) {
    case 0:
      return { tag: "GenericPersistedScheduleEvent", val: readGenericPersistedScheduleEvent2(bc) };
    default: {
      bc.offset = offset;
      throw new bare5.BareError(offset, "invalid tag");
    }
  }
}
function writePersistedScheduleEventKind2(bc, x) {
  switch (x.tag) {
    case "GenericPersistedScheduleEvent": {
      bare5.writeU8(bc, 0);
      writeGenericPersistedScheduleEvent2(bc, x.val);
      break;
    }
  }
}
function readPersistedScheduleEvent2(bc) {
  return {
    eventId: bare5.readString(bc),
    timestamp: bare5.readI64(bc),
    kind: readPersistedScheduleEventKind2(bc)
  };
}
function writePersistedScheduleEvent2(bc, x) {
  bare5.writeString(bc, x.eventId);
  bare5.writeI64(bc, x.timestamp);
  writePersistedScheduleEventKind2(bc, x.kind);
}
function readPersistedHibernatableWebSocket(bc) {
  return {
    requestId: bare5.readData(bc),
    lastSeenTimestamp: bare5.readI64(bc),
    msgIndex: bare5.readI64(bc)
  };
}
function writePersistedHibernatableWebSocket(bc, x) {
  bare5.writeData(bc, x.requestId);
  bare5.writeI64(bc, x.lastSeenTimestamp);
  bare5.writeI64(bc, x.msgIndex);
}
function read23(bc) {
  const len = bare5.readUintSafe(bc);
  if (len === 0) {
    return [];
  }
  const result = [readPersistedConnection2(bc)];
  for (let i = 1; i < len; i++) {
    result[i] = readPersistedConnection2(bc);
  }
  return result;
}
function write23(bc, x) {
  bare5.writeUintSafe(bc, x.length);
  for (let i = 0; i < x.length; i++) {
    writePersistedConnection2(bc, x[i]);
  }
}
function read33(bc) {
  const len = bare5.readUintSafe(bc);
  if (len === 0) {
    return [];
  }
  const result = [readPersistedScheduleEvent2(bc)];
  for (let i = 1; i < len; i++) {
    result[i] = readPersistedScheduleEvent2(bc);
  }
  return result;
}
function write33(bc, x) {
  bare5.writeUintSafe(bc, x.length);
  for (let i = 0; i < x.length; i++) {
    writePersistedScheduleEvent2(bc, x[i]);
  }
}
function read42(bc) {
  const len = bare5.readUintSafe(bc);
  if (len === 0) {
    return [];
  }
  const result = [readPersistedHibernatableWebSocket(bc)];
  for (let i = 1; i < len; i++) {
    result[i] = readPersistedHibernatableWebSocket(bc);
  }
  return result;
}
function write42(bc, x) {
  bare5.writeUintSafe(bc, x.length);
  for (let i = 0; i < x.length; i++) {
    writePersistedHibernatableWebSocket(bc, x[i]);
  }
}
function readPersistedActor2(bc) {
  return {
    input: read15(bc),
    hasInitialized: bare5.readBool(bc),
    state: bare5.readData(bc),
    connections: read23(bc),
    scheduledEvents: read33(bc),
    hibernatableWebSockets: read42(bc)
  };
}
function writePersistedActor2(bc, x) {
  write15(bc, x.input);
  bare5.writeBool(bc, x.hasInitialized);
  bare5.writeData(bc, x.state);
  write23(bc, x.connections);
  write33(bc, x.scheduledEvents);
  write42(bc, x.hibernatableWebSockets);
}
function encodePersistedActor2(x) {
  const bc = new bare5.ByteCursor(
    new Uint8Array(config5.initialBufferLength),
    config5
  );
  writePersistedActor2(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodePersistedActor2(bytes) {
  const bc = new bare5.ByteCursor(bytes, config5);
  const result = readPersistedActor2(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare5.BareError(bc.offset, "remaining bytes");
  }
  return result;
}

// dist/schemas/actor-persist/v3.ts
import * as bare6 from "@rivetkit/bare-ts";
var config6 = /* @__PURE__ */ bare6.Config({});
function readGatewayId(bc) {
  return bare6.readFixedData(bc, 4);
}
function writeGatewayId(bc, x) {
  assert(x.byteLength === 4);
  bare6.writeFixedData(bc, x);
}
function readRequestId(bc) {
  return bare6.readFixedData(bc, 4);
}
function writeRequestId(bc, x) {
  assert(x.byteLength === 4);
  bare6.writeFixedData(bc, x);
}
function readCbor(bc) {
  return bare6.readData(bc);
}
function writeCbor(bc, x) {
  bare6.writeData(bc, x);
}
function readSubscription(bc) {
  return {
    eventName: bare6.readString(bc)
  };
}
function writeSubscription(bc, x) {
  bare6.writeString(bc, x.eventName);
}
function read06(bc) {
  const len = bare6.readUintSafe(bc);
  if (len === 0) {
    return [];
  }
  const result = [readSubscription(bc)];
  for (let i = 1; i < len; i++) {
    result[i] = readSubscription(bc);
  }
  return result;
}
function write06(bc, x) {
  bare6.writeUintSafe(bc, x.length);
  for (let i = 0; i < x.length; i++) {
    writeSubscription(bc, x[i]);
  }
}
function read16(bc) {
  const len = bare6.readUintSafe(bc);
  const result = /* @__PURE__ */ new Map();
  for (let i = 0; i < len; i++) {
    const offset = bc.offset;
    const key = bare6.readString(bc);
    if (result.has(key)) {
      bc.offset = offset;
      throw new bare6.BareError(offset, "duplicated key");
    }
    result.set(key, bare6.readString(bc));
  }
  return result;
}
function write16(bc, x) {
  bare6.writeUintSafe(bc, x.size);
  for (const kv of x) {
    bare6.writeString(bc, kv[0]);
    bare6.writeString(bc, kv[1]);
  }
}
function readConn(bc) {
  return {
    id: bare6.readString(bc),
    parameters: readCbor(bc),
    state: readCbor(bc),
    subscriptions: read06(bc),
    gatewayId: readGatewayId(bc),
    requestId: readRequestId(bc),
    serverMessageIndex: bare6.readU16(bc),
    clientMessageIndex: bare6.readU16(bc),
    requestPath: bare6.readString(bc),
    requestHeaders: read16(bc)
  };
}
function writeConn(bc, x) {
  bare6.writeString(bc, x.id);
  writeCbor(bc, x.parameters);
  writeCbor(bc, x.state);
  write06(bc, x.subscriptions);
  writeGatewayId(bc, x.gatewayId);
  writeRequestId(bc, x.requestId);
  bare6.writeU16(bc, x.serverMessageIndex);
  bare6.writeU16(bc, x.clientMessageIndex);
  bare6.writeString(bc, x.requestPath);
  write16(bc, x.requestHeaders);
}
function encodeConn(x) {
  const bc = new bare6.ByteCursor(
    new Uint8Array(config6.initialBufferLength),
    config6
  );
  writeConn(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeConn(bytes) {
  const bc = new bare6.ByteCursor(bytes, config6);
  const result = readConn(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare6.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function read24(bc) {
  return bare6.readBool(bc) ? readCbor(bc) : null;
}
function write24(bc, x) {
  bare6.writeBool(bc, x !== null);
  if (x !== null) {
    writeCbor(bc, x);
  }
}
function readScheduleEvent(bc) {
  return {
    eventId: bare6.readString(bc),
    timestamp: bare6.readI64(bc),
    action: bare6.readString(bc),
    args: read24(bc)
  };
}
function writeScheduleEvent(bc, x) {
  bare6.writeString(bc, x.eventId);
  bare6.writeI64(bc, x.timestamp);
  bare6.writeString(bc, x.action);
  write24(bc, x.args);
}
function read34(bc) {
  const len = bare6.readUintSafe(bc);
  if (len === 0) {
    return [];
  }
  const result = [readScheduleEvent(bc)];
  for (let i = 1; i < len; i++) {
    result[i] = readScheduleEvent(bc);
  }
  return result;
}
function write34(bc, x) {
  bare6.writeUintSafe(bc, x.length);
  for (let i = 0; i < x.length; i++) {
    writeScheduleEvent(bc, x[i]);
  }
}
function readActor(bc) {
  return {
    input: read24(bc),
    hasInitialized: bare6.readBool(bc),
    state: readCbor(bc),
    scheduledEvents: read34(bc)
  };
}
function writeActor(bc, x) {
  write24(bc, x.input);
  bare6.writeBool(bc, x.hasInitialized);
  writeCbor(bc, x.state);
  write34(bc, x.scheduledEvents);
}
function encodeActor(x) {
  const bc = new bare6.ByteCursor(
    new Uint8Array(config6.initialBufferLength),
    config6
  );
  writeActor(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeActor(bytes) {
  const bc = new bare6.ByteCursor(bytes, config6);
  const result = readActor(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare6.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function assert(condition, message) {
  if (!condition) throw new Error(message ?? "Assertion failed");
}

// dist/schemas/actor-persist/v4.ts
import * as bare7 from "@rivetkit/bare-ts";
var config7 = /* @__PURE__ */ bare7.Config({});
function readGatewayId2(bc) {
  return bare7.readFixedData(bc, 4);
}
function writeGatewayId2(bc, x) {
  assert2(x.byteLength === 4);
  bare7.writeFixedData(bc, x);
}
function readRequestId2(bc) {
  return bare7.readFixedData(bc, 4);
}
function writeRequestId2(bc, x) {
  assert2(x.byteLength === 4);
  bare7.writeFixedData(bc, x);
}
function readCbor2(bc) {
  return bare7.readData(bc);
}
function writeCbor2(bc, x) {
  bare7.writeData(bc, x);
}
function readSubscription2(bc) {
  return {
    eventName: bare7.readString(bc)
  };
}
function writeSubscription2(bc, x) {
  bare7.writeString(bc, x.eventName);
}
function read07(bc) {
  const len = bare7.readUintSafe(bc);
  if (len === 0) {
    return [];
  }
  const result = [readSubscription2(bc)];
  for (let i = 1; i < len; i++) {
    result[i] = readSubscription2(bc);
  }
  return result;
}
function write07(bc, x) {
  bare7.writeUintSafe(bc, x.length);
  for (let i = 0; i < x.length; i++) {
    writeSubscription2(bc, x[i]);
  }
}
function read17(bc) {
  const len = bare7.readUintSafe(bc);
  const result = /* @__PURE__ */ new Map();
  for (let i = 0; i < len; i++) {
    const offset = bc.offset;
    const key = bare7.readString(bc);
    if (result.has(key)) {
      bc.offset = offset;
      throw new bare7.BareError(offset, "duplicated key");
    }
    result.set(key, bare7.readString(bc));
  }
  return result;
}
function write17(bc, x) {
  bare7.writeUintSafe(bc, x.size);
  for (const kv of x) {
    bare7.writeString(bc, kv[0]);
    bare7.writeString(bc, kv[1]);
  }
}
function readConn2(bc) {
  return {
    id: bare7.readString(bc),
    parameters: readCbor2(bc),
    state: readCbor2(bc),
    subscriptions: read07(bc),
    gatewayId: readGatewayId2(bc),
    requestId: readRequestId2(bc),
    serverMessageIndex: bare7.readU16(bc),
    clientMessageIndex: bare7.readU16(bc),
    requestPath: bare7.readString(bc),
    requestHeaders: read17(bc)
  };
}
function writeConn2(bc, x) {
  bare7.writeString(bc, x.id);
  writeCbor2(bc, x.parameters);
  writeCbor2(bc, x.state);
  write07(bc, x.subscriptions);
  writeGatewayId2(bc, x.gatewayId);
  writeRequestId2(bc, x.requestId);
  bare7.writeU16(bc, x.serverMessageIndex);
  bare7.writeU16(bc, x.clientMessageIndex);
  bare7.writeString(bc, x.requestPath);
  write17(bc, x.requestHeaders);
}
function encodeConn2(x) {
  const bc = new bare7.ByteCursor(
    new Uint8Array(config7.initialBufferLength),
    config7
  );
  writeConn2(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeConn2(bytes) {
  const bc = new bare7.ByteCursor(bytes, config7);
  const result = readConn2(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare7.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function read25(bc) {
  return bare7.readBool(bc) ? readCbor2(bc) : null;
}
function write25(bc, x) {
  bare7.writeBool(bc, x !== null);
  if (x !== null) {
    writeCbor2(bc, x);
  }
}
function readScheduleEvent2(bc) {
  return {
    eventId: bare7.readString(bc),
    timestamp: bare7.readI64(bc),
    action: bare7.readString(bc),
    args: read25(bc)
  };
}
function writeScheduleEvent2(bc, x) {
  bare7.writeString(bc, x.eventId);
  bare7.writeI64(bc, x.timestamp);
  bare7.writeString(bc, x.action);
  write25(bc, x.args);
}
function read35(bc) {
  const len = bare7.readUintSafe(bc);
  if (len === 0) {
    return [];
  }
  const result = [readScheduleEvent2(bc)];
  for (let i = 1; i < len; i++) {
    result[i] = readScheduleEvent2(bc);
  }
  return result;
}
function write35(bc, x) {
  bare7.writeUintSafe(bc, x.length);
  for (let i = 0; i < x.length; i++) {
    writeScheduleEvent2(bc, x[i]);
  }
}
function readActor2(bc) {
  return {
    input: read25(bc),
    hasInitialized: bare7.readBool(bc),
    state: readCbor2(bc),
    scheduledEvents: read35(bc)
  };
}
function writeActor2(bc, x) {
  write25(bc, x.input);
  bare7.writeBool(bc, x.hasInitialized);
  writeCbor2(bc, x.state);
  write35(bc, x.scheduledEvents);
}
function encodeActor2(x) {
  const bc = new bare7.ByteCursor(
    new Uint8Array(config7.initialBufferLength),
    config7
  );
  writeActor2(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeActor2(bytes) {
  const bc = new bare7.ByteCursor(bytes, config7);
  const result = readActor2(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare7.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function readQueueMetadata(bc) {
  return {
    nextId: bare7.readU64(bc),
    size: bare7.readU32(bc)
  };
}
function writeQueueMetadata(bc, x) {
  bare7.writeU64(bc, x.nextId);
  bare7.writeU32(bc, x.size);
}
function encodeQueueMetadata(x) {
  const bc = new bare7.ByteCursor(
    new Uint8Array(config7.initialBufferLength),
    config7
  );
  writeQueueMetadata(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeQueueMetadata(bytes) {
  const bc = new bare7.ByteCursor(bytes, config7);
  const result = readQueueMetadata(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare7.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function read43(bc) {
  return bare7.readBool(bc) ? bare7.readU32(bc) : null;
}
function write43(bc, x) {
  bare7.writeBool(bc, x !== null);
  if (x !== null) {
    bare7.writeU32(bc, x);
  }
}
function read5(bc) {
  return bare7.readBool(bc) ? bare7.readI64(bc) : null;
}
function write5(bc, x) {
  bare7.writeBool(bc, x !== null);
  if (x !== null) {
    bare7.writeI64(bc, x);
  }
}
function read6(bc) {
  return bare7.readBool(bc) ? bare7.readBool(bc) : null;
}
function write6(bc, x) {
  bare7.writeBool(bc, x !== null);
  if (x !== null) {
    bare7.writeBool(bc, x);
  }
}
function readQueueMessage(bc) {
  return {
    name: bare7.readString(bc),
    body: readCbor2(bc),
    createdAt: bare7.readI64(bc),
    failureCount: read43(bc),
    availableAt: read5(bc),
    inFlight: read6(bc),
    inFlightAt: read5(bc)
  };
}
function writeQueueMessage(bc, x) {
  bare7.writeString(bc, x.name);
  writeCbor2(bc, x.body);
  bare7.writeI64(bc, x.createdAt);
  write43(bc, x.failureCount);
  write5(bc, x.availableAt);
  write6(bc, x.inFlight);
  write5(bc, x.inFlightAt);
}
function encodeQueueMessage(x) {
  const bc = new bare7.ByteCursor(
    new Uint8Array(config7.initialBufferLength),
    config7
  );
  writeQueueMessage(bc, x);
  return new Uint8Array(bc.view.buffer, bc.view.byteOffset, bc.offset);
}
function decodeQueueMessage(bytes) {
  const bc = new bare7.ByteCursor(bytes, config7);
  const result = readQueueMessage(bc);
  if (bc.offset < bc.view.byteLength) {
    throw new bare7.BareError(bc.offset, "remaining bytes");
  }
  return result;
}
function assert2(condition, message) {
  if (!condition) throw new Error(message ?? "Assertion failed");
}

// src/schemas/actor-persist/versioned.ts
var CURRENT_VERSION3 = 4;
var v1ToV22 = (v1Data) => ({
  ...v1Data,
  connections: v1Data.connections.map((conn) => ({
    ...conn,
    hibernatableRequestId: null
  })),
  hibernatableWebSockets: []
});
var v2ToV32 = (v2Data) => {
  const scheduledEvents = v2Data.scheduledEvents.map(
    (event) => {
      if (event.kind.tag === "GenericPersistedScheduleEvent") {
        return {
          eventId: event.eventId,
          timestamp: event.timestamp,
          action: event.kind.val.action,
          args: event.kind.val.args
        };
      }
      throw new Error(`Unknown schedule event kind: ${event.kind.tag}`);
    }
  );
  return {
    input: v2Data.input,
    hasInitialized: v2Data.hasInitialized,
    state: v2Data.state,
    scheduledEvents
  };
};
var v3ToV4 = (v3Data) => {
  return v3Data;
};
var v4ToV3 = (v4Data) => {
  return v4Data;
};
var v3ToV22 = (v3Data) => {
  const scheduledEvents = v3Data.scheduledEvents.map((event) => ({
    eventId: event.eventId,
    timestamp: event.timestamp,
    kind: {
      tag: "GenericPersistedScheduleEvent",
      val: {
        action: event.action,
        args: event.args
      }
    }
  }));
  return {
    input: v3Data.input,
    hasInitialized: v3Data.hasInitialized,
    state: v3Data.state,
    scheduledEvents,
    connections: [],
    hibernatableWebSockets: []
  };
};
var v2ToV12 = (v2Data) => {
  return {
    input: v2Data.input,
    hasInitialized: v2Data.hasInitialized,
    state: v2Data.state,
    scheduledEvents: v2Data.scheduledEvents,
    connections: v2Data.connections.map((conn) => {
      const { hibernatableRequestId, ...rest } = conn;
      return rest;
    })
  };
};
var ACTOR_VERSIONED = createVersionedDataHandler2({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 1:
        return decodePersistedActor(bytes);
      case 2:
        return decodePersistedActor2(bytes);
      case 3:
        return decodeActor(bytes);
      case 4:
        return decodeActor2(bytes);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 1:
        return encodePersistedActor(data);
      case 2:
        return encodePersistedActor2(data);
      case 3:
        return encodeActor(data);
      case 4:
        return encodeActor2(data);
      default:
        throw new Error(`Unknown version ${version}`);
    }
  },
  deserializeConverters: () => [v1ToV22, v2ToV32, v3ToV4],
  serializeConverters: () => [v4ToV3, v3ToV22, v2ToV12]
});
var v3ConnToV4 = (v3Data) => {
  return v3Data;
};
var v4ConnToV3 = (v4Data) => {
  return v4Data;
};
var CONN_VERSIONED = createVersionedDataHandler2({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 3:
        return decodeConn(bytes);
      case 4:
        return decodeConn2(bytes);
      default:
        throw new Error(
          `Conn type only exists in version 3+, got version ${version}`
        );
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 3:
        return encodeConn(data);
      case 4:
        return encodeConn2(data);
      default:
        throw new Error(
          `Conn type only exists in version 3+, got version ${version}`
        );
    }
  },
  deserializeConverters: () => [v3ConnToV4],
  serializeConverters: () => [v4ConnToV3]
});
var QUEUE_METADATA_VERSIONED = createVersionedDataHandler2({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 4:
        return decodeQueueMetadata(bytes);
      default:
        throw new Error(
          `QueueMetadata type only exists in version 4+, got version ${version}`
        );
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 4:
        return encodeQueueMetadata(data);
      default:
        throw new Error(
          `QueueMetadata type only exists in version 4+, got version ${version}`
        );
    }
  },
  deserializeConverters: () => [],
  serializeConverters: () => []
});
var QUEUE_MESSAGE_VERSIONED = createVersionedDataHandler2({
  deserializeVersion: (bytes, version) => {
    switch (version) {
      case 4:
        return decodeQueueMessage(bytes);
      default:
        throw new Error(
          `QueueMessage type only exists in version 4+, got version ${version}`
        );
    }
  },
  serializeVersion: (data, version) => {
    switch (version) {
      case 4:
        return encodeQueueMessage(data);
      default:
        throw new Error(
          `QueueMessage type only exists in version 4+, got version ${version}`
        );
    }
  },
  deserializeConverters: () => [],
  serializeConverters: () => []
});

// src/driver-helpers/utils.ts
function serializeEmptyPersistData(input) {
  const persistData = {
    input: input !== void 0 ? bufferToArrayBuffer(cbor7.encode(input)) : null,
    hasInitialized: false,
    state: bufferToArrayBuffer(cbor7.encode(void 0)),
    scheduledEvents: []
  };
  return ACTOR_VERSIONED.serializeWithEmbeddedVersion(
    persistData,
    CURRENT_VERSION3
  );
}
function getInitialActorKvState(input) {
  const persistData = serializeEmptyPersistData(input);
  return [[KEYS.PERSIST_DATA, persistData]];
}

export {
  getRunFunction,
  getRunInspectorConfig,
  ActorConfigSchema,
  createHttpDriver,
  CURRENT_VERSION2 as CURRENT_VERSION,
  TO_SERVER_VERSIONED2 as TO_SERVER_VERSIONED,
  TO_CLIENT_VERSIONED2 as TO_CLIENT_VERSIONED,
  HTTP_ACTION_REQUEST_VERSIONED,
  HTTP_ACTION_RESPONSE_VERSIONED,
  HTTP_QUEUE_SEND_REQUEST_VERSIONED,
  HTTP_QUEUE_SEND_RESPONSE_VERSIONED,
  HTTP_RESPONSE_ERROR_VERSIONED,
  ToClientSchema,
  ToServerSchema,
  HttpActionRequestSchema,
  HttpActionResponseSchema,
  HttpQueueSendRequestSchema,
  HttpQueueSendResponseSchema,
  HttpResponseErrorSchema,
  uint8ArrayToBase64,
  contentTypeForEncoding,
  serializeWithEncoding,
  deserializeWithEncoding,
  loggerWithoutContext,
  assertUnreachable2 as assertUnreachable,
  DeadlineError,
  deadline,
  generateSecureToken,
  isStatePath,
  isConnStatePath,
  EncodingSchema,
  CachedSerializer,
  jsonStringifyCompat,
  CONN_CONNECTED_SYMBOL,
  CONN_SPEAKS_RIVETKIT_SYMBOL,
  CONN_DRIVER_SYMBOL,
  CONN_STATE_MANAGER_SYMBOL,
  CONN_SEND_MESSAGE_SYMBOL,
  Conn,
  ActionContext,
  CURRENT_VERSION3 as CURRENT_VERSION2,
  ACTOR_VERSIONED,
  CONN_VERSIONED,
  QUEUE_METADATA_VERSIONED,
  QUEUE_MESSAGE_VERSIONED,
  BeforeConnectContext,
  ConnectContext,
  CreateConnStateContext,
  RequestContext,
  WebSocketContext,
  serializeActorKey,
  deserializeActorKey,
  getValueLength,
  inputDataToBuffer,
  processMessage,
  PATH_CONNECT,
  PATH_WEBSOCKET_BASE,
  PATH_WEBSOCKET_PREFIX,
  PATH_INSPECTOR_CONNECT,
  HEADER_ACTOR_QUERY,
  HEADER_ENCODING,
  HEADER_CONN_PARAMS,
  HEADER_ACTOR_ID,
  HEADER_RIVET_TOKEN,
  HEADER_RIVET_TARGET,
  HEADER_RIVET_ACTOR,
  WS_PROTOCOL_STANDARD,
  WS_PROTOCOL_TARGET,
  WS_PROTOCOL_ACTOR,
  WS_PROTOCOL_ENCODING,
  WS_PROTOCOL_CONN_PARAMS,
  WS_TEST_PROTOCOL_PATH,
  ALLOWED_PUBLIC_HEADERS,
  handleAction,
  handleQueueSend,
  handleRawRequest,
  ENGINE_PORT,
  ENGINE_ENDPOINT,
  InspectorConfigSchema,
  tryParseEndpoint,
  DriverConfigSchema,
  ActorsSchema,
  TestConfigSchema,
  RegistryConfigSchema,
  buildActorNames,
  DocInspectorConfigSchema,
  DocConfigureRunnerPoolSchema,
  DocServerlessConfigSchema,
  DocRunnerConfigSchema,
  DocRegistryConfigSchema,
  loggerMiddleware,
  handleRouteNotFound,
  handleRouteError,
  handleMetadataRequest,
  handleHealthRequest,
  routeWebSocket,
  parseWebSocketProtocols,
  truncateRawWebSocketPathPrefix,
  createRouter,
  buildManagerRouter,
  getInitialActorKvState
};
//# sourceMappingURL=chunk-KAQ6FLH6.js.map